// Thin API client over the same-origin Cloudflare Worker that backs
// investor-qa.gpp.dev. All endpoints sit under `/v1/*`. The worker reads
// authentication from the Cloudflare Access JWT cookie set on the host;
// the browser's `credentials: "include"` is on by default for same-origin
// requests, so we don't have to do anything special here.

import type { TrustTrace } from "@/lib/trust-trace";
import { normalizeTrace } from "@/lib/trust-trace";
import type {
  HistoricalAnalysisSpec,
  HistoricalDashboardSnapshotV1,
  HistoricalDataset,
  HistoricalDashboardPatch,
} from "@/components/portfolio/historicalAnalytics";
import { isHistoricalRevisionResponse, type HistoricalRevisionMetadata, type HistoricalRevisionResponse } from "@/components/portfolio/historicalDashboardRevisions";

const API_BASE = "";

// ─── Session-expiry backstop (self-hosted auth) ─────────────────────
//
// On any 401 from a same-origin /v1/* call, persist the in-progress chat
// draft and bounce to the worker-rendered /login page. The worker also 302s
// top-level HTML navigations to /login, so this is the in-app XHR/stream
// backstop. It is a no-op under `cf-access` mode (the Cloudflare edge handles
// expiry by bouncing to its own login before a request ever 401s here).
const DRAFT_KEY = "investor-qa.draft";
let draftProvider: (() => string) | null = null;
let redirectingToLogin = false;

/** Register a getter for the in-progress chat draft (called by ChatPage). */
export function registerDraftProvider(fn: (() => string) | null) {
  draftProvider = fn;
}

/** Read (and optionally clear) a draft persisted across a session-expiry bounce. */
export function readSavedDraft(): string {
  try {
    return window.localStorage.getItem(DRAFT_KEY) ?? "";
  } catch {
    return "";
  }
}

export function clearSavedDraft() {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* localStorage unavailable */
  }
}

// ─── Deploy version sentinel (#107 cache-skew) ──────────────────────
//
// The CI deploy writes /version.json = { "build": "<GITHUB_SHA>" } alongside the
// static assets and bakes the SAME id into the bundle (Vite `define __BUILD_ID__`).
// A long-lived tab polls this to notice a fresh deploy and offer a CALM reload
// (never auto-reload — that would nuke a DDQ draft or half-typed chat). Fetched
// same-origin with `cache: "no-store"` so the CDN's cached copy never masks a new
// build; CSP `connect-src 'self'` allows it.
//
// Guard: if the session has expired, the worker 302s HTML navigations to /login,
// but a `fetch` follows redirects and returns the /login HTML with a 200. We
// therefore treat a non-JSON / unparseable body (or any error) as "unknown" and
// return null — the caller shows NO banner rather than a false-positive on a
// logged-out tab.
export async function getBuildVersion(): Promise<string | null> {
  try {
    const resp = await fetch("/version.json", { cache: "no-store" });
    if (!resp.ok) return null;
    const ct = resp.headers.get("content-type") || "";
    if (!ct.includes("application/json") && !ct.includes("text/json")) return null;
    const data = (await resp.json()) as { build?: unknown };
    const build = typeof data?.build === "string" ? data.build.trim() : "";
    return build || null;
  } catch {
    return null;
  }
}

// ─── Impersonation ("View as Standard LP") ──────────────────────────
//
// A GP admin can preview the exact external-LP experience. When active, every
// /v1 request carries `X-Impersonate-Tier: lp_standard`; the worker (which has
// already authenticated the real gp_team user) forces the access tier to the
// floor so gated docs are hidden in content, citations, and provenance. This
// is a pure downgrade — it never grants access — so it's safe even though the
// flag originates client-side. Persisted in localStorage so it survives
// navigation; always cleared on logout / session expiry so a fresh login
// never silently starts impersonating.
const IMPERSONATE_KEY = "investor-qa.impersonate-tier";

function readImpersonationTier(): "lp_standard" | null {
  try {
    return window.localStorage.getItem(IMPERSONATE_KEY) === "lp_standard"
      ? "lp_standard"
      : null;
  } catch {
    return null;
  }
}

let impersonationTier: "lp_standard" | null = readImpersonationTier();

export function getImpersonationTier(): "lp_standard" | null {
  return impersonationTier;
}

export function setImpersonationTier(tier: "lp_standard" | null) {
  impersonationTier = tier;
  try {
    if (tier) window.localStorage.setItem(IMPERSONATE_KEY, tier);
    else window.localStorage.removeItem(IMPERSONATE_KEY);
  } catch {
    /* localStorage unavailable */
  }
}

/** Headers to merge into every /v1 request when impersonation is active. */
export function impersonationHeaders(): Record<string, string> {
  return impersonationTier ? { "X-Impersonate-Tier": impersonationTier } : {};
}

/**
 * A failed /v1 request. `status` and `payload` are the fields callers have always read
 * (`isNotFound`, the DDQ review gate); `code` is `payload.code` when the server sent a
 * non-empty string code, else null.
 */
export class ApiError extends Error {
  status: number;
  code: string | null;
  payload: Record<string, unknown>;

  constructor(status: number, payload: Record<string, unknown>) {
    const text = (k: string) => (typeof payload[k] === "string" && payload[k] ? (payload[k] as string) : null);
    super(text("error") ?? text("message") ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = text("code");
    this.payload = payload;
  }
}

/** An engine warning or schema error as the forecast routes return them (shared/forecast). */
type ForecastSchemaError = { path: string; code: string; message: string };
type ForecastEngineWarning = import("../../../shared/forecast/engine.js").EngineWarning;

/** The extra fields the forecast routes put on an error body (reporting-worker techfund-forecast.js). */
export type ForecastApiError = ApiError & {
  payload: {
    cause?: string | null;
    fix?: string | null;
    errors?: ForecastSchemaError[];
    blocking?: ForecastEngineWarning[];
    /** Absent when Postgres serialization (40001) raised the conflict: never rely on it. */
    currentRevision?: number;
  };
};

const unauthorizedHooks = new Set<() => void>();

/**
 * Run `fn` just before a session-expiry redirect to /login (the set editor flushes its
 * unsaved work to sessionStorage here). Returns an unsubscribe function. A hook that
 * throws never blocks the redirect or the other hooks.
 */
export function onBeforeUnauthorized(fn: () => void): () => void {
  unauthorizedHooks.add(fn);
  return () => {
    unauthorizedHooks.delete(fn);
  };
}

function runUnauthorizedHooks() {
  for (const fn of [...unauthorizedHooks]) {
    try {
      fn();
    } catch {
      /* a failed stash must not keep the user on a dead session */
    }
  }
}

/** Persist the current draft, run the onBeforeUnauthorized hooks and redirect to /login. Guards against loops. */
export function handleUnauthorized() {
  if (redirectingToLogin) return;
  if (typeof window === "undefined") return;
  runUnauthorizedHooks();
  setImpersonationTier(null); // a fresh login must never start impersonating
  try {
    const draft = draftProvider?.() ?? "";
    if (draft.trim()) window.localStorage.setItem(DRAFT_KEY, draft);
  } catch {
    /* ignore */
  }
  if (window.location.pathname !== "/login") {
    redirectingToLogin = true;
    try {
      window.location.assign("/login");
    } catch {
      /* jsdom / non-browser: ignore */
    }
  }
}

/** POST /v1/auth/logout — delete the server session row + clear the cookie. */
export function logout() {
  setImpersonationTier(null); // never carry a preview state across a re-login
  return request<{ ok: true }>("/v1/auth/logout", { method: "POST" });
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      "X-Historical-Response-Version": "1",
      ...impersonationHeaders(),
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!resp.ok) {
    // Read the body first: whether a 401 is a dead session depends on it.
    const text = await resp.text();
    let payload: Record<string, unknown> = {};
    try {
      const parsed: unknown = JSON.parse(text);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) payload = parsed as Record<string, unknown>;
    } catch {
      /* keep defaults */
    }
    if (resp.status === 401) {
      // A 401 from the reporting PROXY is an upstream service failure (e.g. a
      // stale REPORTING_INTERNAL_TOKEN on the Pages worker), NOT a dead user
      // session — /v1/me still returns 200 for the same user. Redirecting to
      // /login here signs a fully-authenticated user out of the whole suite
      // (hit on staging, 2026-07-07 QA). Let those 401s propagate to the
      // view's error state instead.
      const isReportingProxy = /^\/v1\/(gp\/)?reporting\//.test(path);
      // A forecast write's 401 with a code is the reporting Worker refusing the
      // capability or the actor assertion (forecast-proxy.js); the Pages session
      // 401 has no code. Only the session 401 signs the user out.
      const isForecastWrite = /^\/v1\/gp\/portfolio\/techfund\/forecast\//.test(path);
      const upstreamCoded = isForecastWrite && typeof payload.code === "string" && payload.code !== "";
      if (!upstreamCoded && !isReportingProxy) handleUnauthorized();
    }
    throw new ApiError(resp.status, payload);
  }
  if (resp.status === 204) return undefined as unknown as T;
  return (await resp.json()) as T;
}

// ─── Funds + identity ──────────────────────────────────────────────

export type AccessTier = "lp_standard" | "lp_extended";

export type Fund = {
  id: string;
  name: string;
  description?: string | null;
  display_order?: number;
  active?: boolean;
  // Present on /v1/me's entitled_funds entries; absent on /v1/funds (the
  // raw catalog endpoint). lp_standard is the safe default if absent.
  access_tier?: AccessTier;
};

export type Me = {
  email: string;
  // Real role — stays "gp_team" even while impersonating, so the UI can keep
  // showing the GP-only "View as LP" toggle. Use `impersonating` to tell
  // whether the session is currently scoped down to a standard LP.
  role: "gp_team" | "lp";
  entitled_funds: Fund[];
  has_extended_access?: boolean;
  ddq_upload_enabled?: boolean;
  impersonating?: boolean;
  impersonated_tier?: AccessTier | null;
  historical_agent_enabled?: boolean;
  historical_response_v1?: boolean;
  historical_analyst_v1?: boolean;
  historical_analyst_v2?: boolean;
  historical_analyst_read_v1?: boolean;
  // Tech Fund ▸ Forward value tab: gp_team, flag on, never while impersonating.
  forward_value_enabled?: boolean;
  // Forward value set editor: this deployment can sign forecast writes for this person
  // (production or local-loopback, not an API key, secrets present). False on staging.
  // A UI hint only; every write is still authorized server-side (D8).
  forward_value_writable?: boolean;
};

export function getMe() {
  return request<Me>("/v1/me");
}

export function getFunds() {
  return request<{ funds: Fund[] }>("/v1/funds");
}

// ─── Chat / conversations / sources ────────────────────────────────

export type SourceAccessLevel = "public" | "extended" | "restricted";

export type ChatSource = {
  index: number;
  tag?: string;
  document_id: string;
  document_name: string;
  file_type?: string;
  file_size?: number;
  sensitivity?: number;
  access_level?: SourceAccessLevel;
  request_access_email?: string | null;
  // Corpus sections (e.g. "5. Asset Portfolio › 5.1 Portfolio Summary")
  // where this tag appeared in the retrieved chunks. Surfaced as small
  // chips on the source card in the slide-out so the LP can see which
  // part of the corpus the citation came from.
  referenced_in?: string[];
  section?: string | null;
  excerpt?: string | null;
};

// WS4 — a reasoning "step" event on the chat stream (#5 chat strip / #4 trace).
// `id` is a stable machine key (embed|retrieve|rerank|filter|registry|generate|
// verify|no_info); `label` is the tier-safe human string the strip renders.
// Only present when the worker's STREAM_STEPS flag is on; unknown SSE types are
// try/catch-skipped, so this is backward-compatible.
export type ChatStep = {
  type: "step";
  id: string;
  label: string;
  status: "running" | "done";
  detail?: string;
};

// WS4 — a machine-readable {entity, attribute, value, source} claim the answer
// makes, valued from the verified registry. The worker ALREADY sends these on
// the final-sources event (chat.js); the client previously dropped them.
export type ChatBinding = {
  entity: string;
  attribute: string;
  value: string;
  source: string;
  as_of?: string;
  unverified?: boolean;
};

export type ChatSyncResponse = {
  answer: string;
  sources: ChatSource[];
  cited_tags: string[];
  conversation_id: string;
  model: string;
  usage?: { prompt_tokens?: number; completion_tokens?: number };
};

export function postChatSync(
  query: string,
  fund: string,
  conversation_id?: string | null,
) {
  return request<ChatSyncResponse>("/v1/chat/sync", {
    method: "POST",
    body: JSON.stringify({ query, conversation_id, fund }),
  });
}

// Streaming chat: returns a ReadableStream of SSE chunks. Caller parses
// `data:` lines and reacts to `{type: "sources" | "content" | "final-sources"}`
// payloads. Used by the live chat surface for typing-indicator UX.
export async function postChatStream(
  query: string,
  fund: string,
  conversation_id?: string | null,
) {
  const resp = await fetch("/v1/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...impersonationHeaders() },
    body: JSON.stringify({ query, conversation_id, fund }),
  });
  if (!resp.ok || !resp.body) {
    if (resp.status === 401) handleUnauthorized();
    const text = await resp.text().catch(() => "");
    throw new Error(`Chat stream failed: ${resp.status} ${text}`);
  }
  return resp.body;
}

// Higher-level streaming wrapper: parses SSE events from /v1/chat and fires
// callbacks as content tokens land + sources resolve. Returns a Promise that
// resolves to the final assembled answer + final-sources once the stream
// finishes (or rejects on a network/server error).
//
// Event shapes from the worker (see worker/src/chat.js):
//   { type: "sources",       sources: [...], conversation_id: "..." }        — chunk-based, sent before tokens
//   { type: "step",          id, label, status, detail? }                     — WS4 reasoning step (STREAM_STEPS on)
//   { type: "content",       content: "..." }                                 — token delta
//   { type: "final-sources", sources: [...], cited_tags: [...], bindings }    — resolved-tag, sent before [DONE]
//   { type: "trace",         trace: TrustTrace }                              — WS4 TrustConsole trace (STREAM_STEPS on)
//   "[DONE]"                                                                  — sentinel
export async function streamChat(
  query: string,
  fund: string,
  conversation_id: string | null | undefined,
  handlers: {
    onConversationId?: (id: string) => void;
    onInitialSources?: (sources: ChatSource[]) => void;
    onContent?: (delta: string, accumulated: string) => void;
    onFinalSources?: (sources: ChatSource[], citedTags: string[], messageId?: string) => void;
    // WS4 — optional; fire only when the worker's STREAM_STEPS flag is on.
    onStep?: (step: ChatStep) => void;
    onTrace?: (trace: TrustTrace) => void;
    // WS4 — bindings ride the final-sources event; the client previously dropped them.
    onBindings?: (bindings: ChatBinding[]) => void;
  },
  signal?: AbortSignal,
): Promise<{ answer: string; sources: ChatSource[]; cited_tags: string[]; message_id?: string; bindings: ChatBinding[] }> {
  const resp = await fetch("/v1/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...impersonationHeaders() },
    body: JSON.stringify({ query, conversation_id, fund }),
    signal,
  });
  if (!resp.ok || !resp.body) {
    if (resp.status === 401) handleUnauthorized();
    const text = await resp.text().catch(() => "");
    throw new Error(`Chat stream failed: ${resp.status} ${text}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let answer = "";
  let finalSources: ChatSource[] = [];
  let initialSources: ChatSource[] = [];
  let citedTags: string[] = [];
  let messageId: string | undefined;
  let bindings: ChatBinding[] = [];
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const raw of lines) {
      if (!raw.startsWith("data: ")) continue;
      const payload = raw.slice(6).trim();
      if (!payload) continue;
      if (payload === "[DONE]") continue;
      try {
        const ev = JSON.parse(payload) as
          | { type: "sources"; sources: ChatSource[]; conversation_id?: string }
          | { type: "content"; content: string }
          | { type: "final-sources"; sources: ChatSource[]; cited_tags: string[]; message_id?: string; bindings?: ChatBinding[] }
          | { type: "step"; id: string; label: string; status: "running" | "done"; detail?: string }
          | { type: "trace"; trace: TrustTrace };
        if (ev.type === "sources") {
          initialSources = ev.sources || [];
          if (ev.conversation_id) handlers.onConversationId?.(ev.conversation_id);
          handlers.onInitialSources?.(initialSources);
        } else if (ev.type === "content") {
          answer += ev.content;
          handlers.onContent?.(ev.content, answer);
        } else if (ev.type === "step") {
          // WS4 reasoning step (only present when STREAM_STEPS is on).
          handlers.onStep?.({ type: "step", id: ev.id, label: ev.label, status: ev.status, detail: ev.detail });
        } else if (ev.type === "final-sources") {
          finalSources = ev.sources || [];
          citedTags = ev.cited_tags || [];
          messageId = ev.message_id;
          bindings = ev.bindings || [];
          handlers.onFinalSources?.(finalSources, citedTags, messageId);
          if (ev.bindings) handlers.onBindings?.(ev.bindings);
        } else if (ev.type === "trace") {
          // WS4 TrustConsole trace (only present when STREAM_STEPS is on).
          // WS-T1: normalize the raw trace to fill null/undefined fields with defaults.
          handlers.onTrace?.(normalizeTrace(ev.trace));
        }
      } catch {
        /* skip unparseable line — usually OpenRouter keep-alive comments */
      }
    }
  }
  return {
    answer,
    sources: finalSources.length > 0 ? finalSources : initialSources,
    cited_tags: citedTags,
    message_id: messageId,
    bindings,
  };
}

export type ConversationSummary = {
  id: string;
  title: string;
  user_email: string;
  // 'ddq' for DDQ-upload conversations, falsy/'chat' for standard ones.
  // Used by the rail to swap icons and tag DDQ rows.
  kind?: string | null;
  created_at: string;
  updated_at: string;
};

export type ConversationDetail = ConversationSummary & {
  messages: {
    id: string;
    role: "user" | "assistant";
    content: string;
    sources?: ChatSource[];
    // DDQ-response messages carry a structured payload that the chat UI
    // renders as a collapsible accordion instead of a flat answer bubble.
    kind?: "text" | "ddq_response";
    // Forward-declared above (depends on DDQStructuredPayload defined later);
    // typed as `any` here to keep the ConversationDetail block self-contained.
    structured_payload?: unknown;
    created_at: string;
  }[];
};

export function getConversations(limit = 30, fund?: string) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (fund) qs.set("fund", fund);
  return request<{ conversations: ConversationSummary[] }>(
    `/v1/conversations?${qs.toString()}`,
  );
}

export function getConversation(id: string) {
  return request<ConversationDetail>(`/v1/conversations/${id}`);
}

// ─── Documents ─────────────────────────────────────────────────────

export type DocumentRow = {
  id: string;
  name: string;
  file_type: string;
  file_size: number | null;
  chunk_count: number | null;
  status: string;
  sensitivity?: number;
  is_gated?: boolean;
  fund_ids?: string[];
  created_at: string;
};

export function listDocuments(fund?: string) {
  const qs = fund ? `?fund=${fund}` : "";
  return request<{ documents: DocumentRow[] }>(`/v1/documents${qs}`);
}

export type DownloadResponse =
  | { document_name: string; download_url: string; expires_in: number }
  | {
      error: string;
      access_level: "restricted";
      document_name: string;
      sensitivity: number;
      request_access_email: string;
      message: string;
    };

export type PreviewResponse =
  | { document_name: string; preview_url: string; expires_in: number }
  | {
      error: string;
      access_level: "restricted";
      document_name: string;
      sensitivity: number;
      request_access_email: string;
      message: string;
    }
  | {
      error: string;
      message?: string;
    };

export async function getDocumentPreview(documentId: string): Promise<PreviewResponse> {
  const resp = await fetch(`/v1/documents/${documentId}/preview`, {
    headers: { ...impersonationHeaders() },
  });
  return (await resp.json()) as PreviewResponse;
}

export async function getDocumentDownload(documentId: string): Promise<DownloadResponse> {
  const resp = await fetch(`/v1/documents/${documentId}/download`, {
    headers: { ...impersonationHeaders() },
  });
  return (await resp.json()) as DownloadResponse;
}

// ─── Feedback ──────────────────────────────────────────────────────

// `correction_sources` was a dead column with a live handler: `feedback.correction_sources`
// exists (sql/001:77) and the admin promote path reads it, but nothing ever
// populated it. #145 wires it through.
export function postFeedback(
  message_id: string,
  rating: "up" | "down",
  correction?: string,
  correction_sources?: string,
) {
  return request<{ feedback_id: string; status: string }>("/v1/feedback", {
    method: "POST",
    body: JSON.stringify({ message_id, rating, correction, correction_sources }),
  });
}

// ─── UAT / product feedback (floating widget) ──────────────────────
//
// `submitUatFeedback` posts to /v1/uat-feedback (role 'any' — any authenticated
// user, including LPs if they ever access the suite). The suite app runs under
// the magic-link session (app-auth), which is the same session used by /v1/me
// and /v1/gp/*. We call /v1/uat-feedback directly (mirrors postFeedback's
// pattern, which also calls /v1/feedback directly, not /v1/gp/feedback).
// `getUatFeedback` is gp_team-only and uses adminBase to pick the right path.

export type UatFeedbackKind = "bug" | "idea" | "question" | "other";

export type UatFeedbackRow = {
  id: string;
  submitted_by: string | null;
  kind: UatFeedbackKind;
  body: string;
  route: string | null;
  app: string;
  status: "open" | "triaged" | "closed";
  created_at: string;
};

export function submitUatFeedback(args: {
  kind: UatFeedbackKind;
  body: string;
  route?: string | null;
}) {
  return request<{ id: string; status: "recorded" }>("/v1/uat-feedback", {
    method: "POST",
    body: JSON.stringify(args),
  });
}
// getUatFeedback is declared after adminBase (defined further below near docBase).

// ─── Admin: invitations ────────────────────────────────────────────

// Per-fund tier on an invitation. Wire format for admin endpoints both
// in payloads (POST /invite, PATCH /funds) and responses.
export type FundEntitlement = {
  id: string;
  access_tier: AccessTier;
};

export type Invitation = {
  id: string;
  lp_email: string | null;
  lp_name: string | null;
  lp_firm: string | null;
  status: "pending" | "accepted" | "revoked";
  invited_by: string;
  expires_at: string | null;
  created_at: string;
  accepted_at: string | null;
  // Per-fund tier objects. Older payloads occasionally returned `string[]`;
  // the UI normalizes both shapes so it can render mixed data during deploys.
  funds?: FundEntitlement[];
};

export function normalizeFundEntitlements(input: unknown): FundEntitlement[] {
  if (!Array.isArray(input)) return [];
  return input
    .map((entry) => {
      if (typeof entry === "string") return { id: entry, access_tier: "lp_standard" as const };
      if (entry && typeof entry === "object" && typeof (entry as FundEntitlement).id === "string") {
        const tier = (entry as FundEntitlement).access_tier === "lp_extended"
          ? "lp_extended"
          : "lp_standard";
        return { id: (entry as FundEntitlement).id, access_tier: tier };
      }
      return null;
    })
    .filter((e): e is FundEntitlement => e !== null);
}

export function getInvitations(
  status?: "pending" | "accepted" | "revoked",
  scope: DocAdminScope = "admin",
) {
  const qs = status ? `?status=${status}` : "";
  return request<{ invitations: Invitation[] }>(
    `${adminBase(scope, "/v1/admin/invitations", "/v1/gp/invitations")}${qs}`,
  );
}

export function createInvitation(
  args: {
    name: string;
    email: string;
    firm?: string;
    funds: FundEntitlement[];
  },
  scope: DocAdminScope = "admin",
) {
  return request<{
    invitation: Invitation;
    access_url: string;
    message: string;
    access_grant_warning: string | null;
  }>(adminBase(scope, "/v1/admin/invite", "/v1/gp/invite"), {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export function updateInvitationFunds(
  invitationId: string,
  funds: FundEntitlement[],
  scope: DocAdminScope = "admin",
) {
  return request<{
    invitation_id: string;
    funds: FundEntitlement[];
    added: string[];
    removed: string[];
    tier_changes: Array<{ fund_id: string; from: AccessTier; to: AccessTier }>;
    status: "accepted" | "revoked";
    status_changed: boolean;
    access_grant_warning: string | null;
  }>(
    `${adminBase(scope, "/v1/admin/invitations", "/v1/gp/invitations")}/${invitationId}/funds`,
    {
      method: "PATCH",
      body: JSON.stringify({ funds }),
    },
  );
}

export function updateInvitationTier(invitationId: string, fundId: string, accessTier: AccessTier) {
  return request<{
    invitation_id: string;
    fund_id: string;
    access_tier: AccessTier;
    changed: boolean;
  }>(`/v1/admin/invitations/${invitationId}/tier`, {
    method: "PATCH",
    body: JSON.stringify({ fund_id: fundId, access_tier: accessTier }),
  });
}

// ─── Admin: team ───────────────────────────────────────────────────

export type TeamMember = {
  id: string;
  name: string;
  email: string;
  role: "admin" | "viewer";
  added_by: string | null;
  created_at: string;
};

export function getTeam(scope: DocAdminScope = "admin") {
  return request<{ team: TeamMember[] }>(
    adminBase(scope, "/v1/admin/team", "/v1/gp/team"),
  );
}

export function addTeamMember(
  name: string,
  email: string,
  role: "admin" | "viewer",
  scope: DocAdminScope = "admin",
) {
  return request<{ member: TeamMember }>(
    adminBase(scope, "/v1/admin/team", "/v1/gp/team"),
    {
      method: "POST",
      body: JSON.stringify({ name, email, role }),
    },
  );
}

export function removeTeamMember(id: string, scope: DocAdminScope = "admin") {
  return request<{ removed: TeamMember }>(
    `${adminBase(scope, "/v1/admin/team", "/v1/gp/team")}/${id}`,
    { method: "DELETE" },
  );
}

// ─── Admin: feedback ───────────────────────────────────────────────

export type FeedbackRow = {
  id: string;
  message_id: string;
  user_email: string;
  rating: "up" | "down";
  correction: string | null;
  question?: string | null;
  ai_answer?: string | null;
  lp_email?: string | null;
  reviewed_by?: string | null;
  promoted_to_golden?: boolean;
  created_at: string;
};

export function getAdminFeedback(limit = 50, scope: DocAdminScope = "admin") {
  return request<{ feedback: FeedbackRow[] }>(
    `${adminBase(scope, "/v1/admin/feedback", "/v1/gp/feedback")}?limit=${limit}`,
  );
}

/**
 * Promote a feedback row into the SERVED investor-golden register (#145 §6).
 *
 * This used to be a misnomer: it flipped `feedback.promoted_to_golden` and wrote
 * an audit row that only the offline `bless-golden.mjs --from-feedback` script
 * consumed — and that script targets `golden_set`, the FILE-BASED eval measuring
 * stick, not `investor_goldens`. Nothing reached the register that
 * `lookup_investor_golden` answers from. It now writes a real, governed **Pending**
 * `investor_goldens` row through the same core as the chat composer, and returns
 * its id. Use `sendFeedbackToEval` for the eval-feedstock path instead.
 */
export function promoteFeedback(feedbackId: string, scope: DocAdminScope = "admin") {
  return request<{
    feedback_id: string;
    golden_id: string;
    action: "created" | "updated_pending" | "proposed_revision";
    version: number;
    requires_cco: boolean;
    status: "Pending";
    promoted_to_golden: true;
    reviewed_by: string;
    reviewed_at: string;
    note: string;
  }>(
    `${adminBase(scope, "/v1/admin/feedback", "/v1/gp/feedback")}/${feedbackId}/promote`,
    { method: "POST" },
  );
}

/**
 * Send a feedback row to the EVAL set as draft feedstock (#145 §6) — the
 * pre-#145 promote behavior, preserved verbatim. Writes one audit row that
 * `scripts/bless-golden.mjs --from-feedback` scaffolds a status='draft' golden
 * file from; the eval harness skips drafts until a human blesses one.
 *
 * Deliberately SEPARATE from `promoteFeedback`: `golden_set` is global,
 * unscoped, and feeds LP-chat few-shot injection, so a correction must never
 * enter the eval baseline as a side effect of banking an answer (sql/026:11-22).
 */
export function sendFeedbackToEval(feedbackId: string, scope: DocAdminScope = "admin") {
  return request<{
    feedback_id: string;
    promoted_to_golden: true;
    reviewed_by: string;
    reviewed_at: string;
    status: "sent_to_eval";
  }>(
    `${adminBase(scope, "/v1/admin/feedback", "/v1/gp/feedback")}/${feedbackId}/send-to-eval`,
    { method: "POST" },
  );
}

// ─── Admin: grounding-monitor drift (WS13a) ────────────────────────

export type GroundingWindowStats = {
  mean_score: number | null; // 0..1, null when nothing scored in the window
  n: number;
  scored: number;
  skipped: number;
  skipped_pct: number; // 0..1
};

export type GroundingGroup = {
  fund_id: string;
  surface: string; // 'chat_sync' | 'chat_stream' | 'ddq'
  last7: GroundingWindowStats;
  prior7: GroundingWindowStats;
  last30: GroundingWindowStats;
  by_day: { day: string; mean_score: number | null; n: number }[];
};

export type GroundingWorstAnswer = {
  fund_id: string | null;
  surface: string;
  score: number;
  ungrounded_claims: string[];
  created_at: string;
  // NOTE: deliberately no `query` field — the worker excludes user question
  // text from the drift payload (it can embed LP-specific portfolio intent).
};

export type GroundingStats = {
  enabled: boolean;
  window_days: number;
  groups: GroundingGroup[];
  worst: GroundingWorstAnswer[];
};

export function getGroundingStats(days = 30, scope: DocAdminScope = "admin") {
  return request<GroundingStats>(
    `${adminBase(scope, "/v1/admin/monitor/grounding", "/v1/gp/monitor/grounding")}?days=${days}`,
  );
}

// ─── Admin: DDQ submissions + analytics (#8 / #16, GP-internal) ─────

export type AdminDDQAnswerSummary = {
  total: number;
  ok: number;
  needs_gp_followup: number;
  errored: number;
};

export type AdminDDQSubmissionRow = {
  id: string;
  user_email: string;
  fund_id: string;
  display_name: string;
  original_filename: string;
  status: DDQStatusResponse["status"];
  progress: { total: number; answered: number; failed: number } | null;
  error_message: string | null;
  conversation_id: string | null;
  created_at: string;
  parsed_at: string | null;
  completed_at: string | null;
  question_count: number;
  selected_count: number | null;
  // null until the run has produced a ddq_response payload
  answers: AdminDDQAnswerSummary | null;
  // R1 queue fields: needs_count = questions not yet accepted/edited;
  // attested_at = latest attestation timestamp, null if not yet attested.
  // Optional (default 0 / null) for back-compat with older API responses and test fixtures.
  needs_count?: number;
  attested_at?: string | null;
};

export type AdminDDQQuestionStatus =
  | "answered"
  | "needs_gp_followup"
  | "error"
  | "unanswered";

export type AdminDDQQuestionDetail = {
  question_id: string;
  section: string;
  question: string;
  sub_questions: string[] | null;
  selected: boolean;
  answer: string | null;
  citations: ChatSource[];
  cited_tags: string[];
  // null = not answered yet; otherwise the pipeline's abstain flag (the
  // per-question confidence signal — no numeric confidence exists)
  needs_gp_followup: boolean | null;
  answer_status: AdminDDQQuestionStatus;
};

export type AdminDDQSubmissionDetail = {
  submission: AdminDDQSubmissionRow;
  questions: AdminDDQQuestionDetail[];
  /** Canonical parsed structure from the LP's original DDQ document. Used by the
   *  Workroom to order questions in the LP's original DDQ order via buildDdqSections.
   *  Null on very old submissions that pre-date the parsed_questions column. */
  parsed_questions: DDQParsedQuestions | null;
  /** Question IDs that were selected for the pipeline run (subset of all parsed
   *  questions). Null means all parsed questions were selected. */
  selected_question_ids: string[] | null;
};

/** DDQ admin endpoints are reachable two ways, like the document admin fns:
 *  the legacy admin app (`/v1/admin/ddq/*`, CF-Access-gated) and the unified
 *  suite (`/v1/gp/ddq/*`, app-auth gp_team-gated, NOT behind CF Access). Default
 *  "admin" keeps every existing caller (admin.html / DDQSubmissionsTab)
 *  byte-for-byte unchanged; the suite passes "gp". Reuses `DocAdminScope`
 *  (defined below near `docBase`). */
const ddqBase = (scope: DocAdminScope = "admin") =>
  scope === "gp" ? "/v1/gp/ddq" : "/v1/admin/ddq";

export function getAdminDDQSubmissions(
  opts: { lpEmail?: string; fund?: string; limit?: number; offset?: number } = {},
  scope: DocAdminScope = "admin",
) {
  const params = new URLSearchParams();
  if (opts.lpEmail) params.set("lp_email", opts.lpEmail);
  if (opts.fund) params.set("fund", opts.fund);
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return request<{ submissions: AdminDDQSubmissionRow[]; total: number; limit: number; offset: number }>(
    `${ddqBase(scope)}/submissions${qs ? `?${qs}` : ""}`,
  );
}

export function getAdminDDQSubmission(id: string, scope: DocAdminScope = "admin") {
  return request<AdminDDQSubmissionDetail>(`${ddqBase(scope)}/submissions/${id}`);
}

export type DDQQuestionCluster = {
  canonical: string;
  display: string;
  count: number;
  submission_count: number;
  lp_count: number;
  fund_ids: string[];
  sections: string[];
  statuses: Record<AdminDDQQuestionStatus, number>;
};

export type DDQCorpusGap = {
  canonical: string;
  display: string;
  gap_count: number;
  attempted_count: number;
  gap_rate: number;
  asked_count: number;
  submission_count: number;
  lp_count: number;
  fund_ids: string[];
  statuses: Record<AdminDDQQuestionStatus, number>;
};

export type DDQAnalytics = {
  submissions_analyzed: number;
  lp_count: number;
  question_count: number;
  cluster_count: number;
  most_asked: DDQQuestionCluster[];
  corpus_gaps: DDQCorpusGap[];
  gap_detection: string; // 'answer_status_v1' today; coverageProbe upgrade later
};

export function getAdminDDQAnalytics(
  opts: { fund?: string; top?: number } = {},
  scope: DocAdminScope = "admin",
) {
  const params = new URLSearchParams();
  if (opts.fund) params.set("fund", opts.fund);
  if (opts.top) params.set("top", String(opts.top));
  const qs = params.toString();
  return request<DDQAnalytics>(`${ddqBase(scope)}/analytics${qs ? `?${qs}` : ""}`);
}

// ─── Admin: logs ───────────────────────────────────────────────────

export type AuditLogRow = {
  id: number;
  user_email: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: unknown;
  ip_address: string | null;
  created_at: string;
};

export function getAuditLogs(limit = 100, scope: DocAdminScope = "admin") {
  return request<{ logs: AuditLogRow[] }>(
    `${adminBase(scope, "/v1/admin/logs", "/v1/gp/logs")}?limit=${limit}`,
  );
}

// ─── Admin: prompt-cache metrics ───────────────────────────────────

export type CacheStats = {
  enabled: boolean;
  window_days: number;
  totals: {
    requests: number;
    cached_requests: number;
    cache_read_tokens: number;
    cache_creation_tokens: number;
  };
  hit_rate: number; // 0..1
  est_cost_saved_usd: number;
  by_day: { day: string; read: number; creation: number; requests: number }[];
};

export function getCacheStats(days = 30, scope: DocAdminScope = "admin") {
  return request<CacheStats>(
    `${adminBase(scope, "/v1/admin/cache-stats", "/v1/gp/cache-stats")}?days=${days}`,
  );
}

export type PrewarmResult = {
  enabled: boolean;
  warmed: number;
  funds: { fund_id: string; ok: boolean; status?: number; error?: string }[];
};

export function prewarmCache(scope: DocAdminScope = "admin") {
  return request<PrewarmResult>(
    adminBase(scope, "/v1/admin/cache/prewarm", "/v1/gp/cache/prewarm"),
    { method: "POST" },
  );
}

// ─── Admin: sensitivity gating ─────────────────────────────────────

export type IngestMethod = "master_corpus" | "raw_doc";

export type DocumentWithGating = DocumentRow & {
  sensitivity: number;
  is_gated: boolean;
  gating_override: boolean;
  gating_updated_by: string | null;
  gating_updated_at: string | null;
  fund_ids?: string[];
  ingest_method?: IngestMethod;
  last_quality_score?: number | null;
  last_quality_check_at?: string | null;
  doc_card_preview?: string | null;
  doc_card_truncated?: boolean;
};

export type QualityEvalResult = {
  document_id: string;
  name: string;
  score: number;
  threshold: number;
  passed: boolean;
  questions_generated: number;
  hits: number;
  per_question: Array<{ question: string; hit: boolean; error?: string }>;
  checked_at: string;
};

/** Document-admin endpoints are reachable two ways: the legacy admin app
 *  (`/v1/admin/*`, CF-Access-gated) and the unified suite (`/v1/gp/*`, app-auth
 *  gp_team-gated, NOT behind CF Access). Default "admin" keeps every existing
 *  caller unchanged; the suite passes "gp". */
export type DocAdminScope = "admin" | "gp";
const docBase = (scope: DocAdminScope = "admin") =>
  scope === "gp" ? "/v1/gp" : "/v1/admin";

/**
 * Generic admin↔suite path picker. The legacy admin app (admin.html / the
 * `admin/*Tab.tsx` components) calls the CF-Access-gated `adminPath`; the unified
 * Platform Suite passes scope "gp" to reach the app-auth `gpPath` mirror (NOT
 * behind CF Access — see the /v1/gp/* block in worker/src/index.js). Default
 * "admin" keeps every existing caller byte-for-byte unchanged. This is the
 * generic form of docBase/ddqBase used by the U4 Admin migration; the two gp
 * paths are NOT always a simple /v1/admin → /v1/gp swap (e.g. the investor
 * golden promotion queue moves /v1/golden/review → /v1/gp/golden/promotion-queue
 * to avoid colliding with the eval /v1/gp/golden/review), so callers pass both
 * paths explicitly. */
const adminBase = (scope: DocAdminScope = "admin", adminPath: string, gpPath: string) =>
  scope === "gp" ? gpPath : adminPath;

// ─── UAT / product feedback — read (gp_team only) ──────────────────
// Declared here (after adminBase) so the adminBase reference resolves.
export function getUatFeedback(scope: DocAdminScope = "gp", limit = 100) {
  return request<{ feedback: UatFeedbackRow[] }>(
    `${adminBase(scope, "/v1/admin/uat-feedback", "/v1/gp/uat-feedback")}?limit=${limit}`,
  );
}

export function runDocumentQualityEval(documentId: string, scope: DocAdminScope = "admin") {
  return request<QualityEvalResult>(
    `${docBase(scope)}/documents/${documentId}/run-quality-eval`,
    { method: "POST" },
  );
}

export function getSensitivityList(scope: DocAdminScope = "admin") {
  return request<{ documents: DocumentWithGating[]; threshold: number }>(
    `${docBase(scope)}/documents/sensitivity`,
  );
}

export function getThreshold() {
  return request<{ threshold: number; would_flip_on_apply: number }>(
    "/v1/admin/settings/sensitivity-threshold",
  );
}

export function setThreshold(threshold: number) {
  return request<{ threshold: number; would_flip_on_apply: number }>(
    "/v1/admin/settings/sensitivity-threshold",
    {
      method: "PUT",
      body: JSON.stringify({ threshold }),
    },
  );
}

export function applyThreshold() {
  return request<{
    threshold: number;
    flipped_count: number;
    flipped: { id: string; name: string; sensitivity: number; is_gated: boolean }[];
  }>("/v1/admin/settings/sensitivity-threshold/apply", { method: "POST" });
}

// FIX 4 — per-audience LP verify figure policy (admin-configurable, no redeploy).
export type VerifyFigurePolicy = "off" | "flag" | "drop";
export type VerifyFigurePolicyConfig = {
  policy: VerifyFigurePolicy;
  threshold: number;
  stored: { policy: string | null; threshold: number | null };
  defaults: { policy: VerifyFigurePolicy; threshold: number };
  env: { policy: string | null; threshold: number | null };
  verify_pass_enabled: boolean;
};

export function getVerifyFigurePolicy() {
  return request<VerifyFigurePolicyConfig>("/v1/admin/settings/verify-figure-policy");
}

export function setVerifyFigurePolicy(body: { policy?: VerifyFigurePolicy; threshold?: number }) {
  return request<VerifyFigurePolicyConfig>("/v1/admin/settings/verify-figure-policy", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function patchDocumentGating(
  id: string,
  body: { sensitivity?: number; is_gated?: boolean },
  scope: DocAdminScope = "admin",
) {
  return request<{ document: DocumentWithGating }>(
    `${docBase(scope)}/documents/${id}/sensitivity`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

export function resetDocumentOverride(id: string, scope: DocAdminScope = "admin") {
  return request<{ document: DocumentWithGating; threshold: number }>(
    `${docBase(scope)}/documents/${id}/reset-override`,
    { method: "POST" },
  );
}

// ─── PDF export ────────────────────────────────────────────────────

export type ConversationExport = {
  title: string;
  generated_at: string;
  report_markdown: string;
};

export function exportConversation(id: string) {
  return request<ConversationExport>(`/v1/conversations/${id}/export`, {
    method: "POST",
  });
}

// ─── Conversation export-link (Markdown / JSON, 5-min single-use) ──

export type ExportLinkFormat = "markdown" | "json";

export type ExportLink = {
  url: string;
  format: ExportLinkFormat;
  expires_in: number;
  expires_at: string;
};

export function createExportLink(id: string, format: ExportLinkFormat) {
  return request<ExportLink>(`/v1/conversations/${id}/export-link`, {
    method: "POST",
    body: JSON.stringify({ format }),
  });
}

// ─── DDQ filled-template export (v1.5, #12) + print view (#15 v1) ──
//
// The owner mints a single-use 5-minute link (same security profile as
// the Markdown/JSON export-link flow); opening it serves the LP's
// original DDQ template with the answers written back in (.xlsx cell
// fill / .docx paragraph insert / clean .docx for PDF originals), or a
// print-optimized HTML view the LP saves as PDF from the browser.
// "template" lets the server resolve the native format from the
// original upload's file type.

export type DDQFilledExportRequestFormat = "template" | "xlsx" | "docx" | "print";

export type DDQFilledExportLink = {
  url: string;
  format: "xlsx" | "docx" | "print";
  // True when the original was a PDF (or the template can't be filled) and
  // the server generates a clean .docx with the Q&A pairs instead.
  template_fallback?: boolean;
  expires_in: number;
  expires_at: string;
};

export function createDDQFilledExportLink(
  submissionId: string,
  format: DDQFilledExportRequestFormat = "template",
) {
  return request<DDQFilledExportLink>(`/v1/ddq/${submissionId}/export-filled-link`, {
    method: "POST",
    body: JSON.stringify({ format }),
  });
}

// ─── DDQ upload ────────────────────────────────────────────────────

export type DDQQuestion = {
  id: string;
  text: string;
  sub_questions?: string[];
  // xlsx-origin DDQs: the cell the question was parsed from (captured at
  // parse time; powers the v1.5 filled-template export write-back).
  loc?: { sheet: string; row: number; col: number };
};

export type DDQSection = {
  name: string;
  questions: DDQQuestion[];
};

export type DDQParsedQuestions = {
  sections: DDQSection[];
  question_count: number;
  truncated?: boolean;
};

export type DDQUploadResponse = {
  submission_id: string;
  fund_id: string;
  display_name: string;
  parsed_questions: DDQParsedQuestions;
  daily_remaining: number;
};

// Re-run metadata stamped by POST /v1/ddq/:id/questions/:qid/regenerate (#14).
export type DDQRerunInfo = {
  count: number;
  last_at: string;
  last_by: string;
};

// Prior answer retained when a question is re-run (bounded audit trail).
export type DDQAnswerHistoryEntry = {
  answer: string;
  citations?: ChatSource[];
  cited_tags?: string[];
  needs_gp_followup?: boolean;
  replaced_at: string;
  replaced_by: string;
};

export type DDQAnsweredQuestion = {
  question_id: string;
  section: string;
  question: string;
  sub_questions?: string[] | null;
  answer: string;
  citations: ChatSource[];
  cited_tags: string[];
  needs_gp_followup: boolean;
  error?: boolean;
  // Present only after a per-question re-run (#14).
  rerun?: DDQRerunInfo;
  history?: DDQAnswerHistoryEntry[];
  // Phase 2: server-emitted provenance tier (WS-W9). When absent, the client
  // derives the two-tier fallback in tierFor() (TierBadge.tsx).
  answer_tier?: "golden" | "sourced" | "needs_gp";
};

export type DDQStructuredPayload = {
  kind: "ddq_response";
  submission_id: string;
  display_name: string;
  fund_id: string;
  question_count: number;
  cancelled?: boolean;
  questions: DDQAnsweredQuestion[];
  // Optimistic-concurrency stamp bumped by each per-question re-run (#14).
  rerun_version?: number;
};

export type DDQStatusResponse = {
  submission_id: string;
  conversation_id: string | null;
  status:
    | "uploaded"
    | "parsing"
    | "parsed"
    | "answering"
    | "completed"
    | "failed"
    | "cancelled";
  progress: { total: number; answered: number; failed: number };
  error_message: string | null;
  display_name: string;
  fund_id: string;
  parsed_questions: DDQParsedQuestions;
  completed_at: string | null;
  structured: { message_id: string; payload: DDQStructuredPayload } | null;
};

export async function uploadDDQ(
  file: File,
  fund: string,
  name?: string,
): Promise<DDQUploadResponse> {
  const form = new FormData();
  form.append("file", file);
  form.append("fund", fund);
  if (name) form.append("name", name);
  const resp = await fetch("/v1/ddq/upload", {
    method: "POST",
    headers: { ...impersonationHeaders() },
    body: form,
  });
  const text = await resp.text();
  let parsed: { error?: string } & Partial<DDQUploadResponse> = {};
  try { parsed = JSON.parse(text); } catch { /* keep defaults */ }
  if (!resp.ok) {
    throw new Error(parsed.error || `Upload failed: ${resp.status}`);
  }
  return parsed as DDQUploadResponse;
}

export function getDDQStatus(submissionId: string) {
  return request<DDQStatusResponse>(`/v1/ddq/${submissionId}/status`);
}

export function cancelDDQ(submissionId: string) {
  return request<{ status: string }>(`/v1/ddq/${submissionId}/cancel`, {
    method: "POST",
  });
}

// ─── DDQ inline follow-up (#17) ─────────────────────────────────────
//
// A follow-up asked from inside an AnswerBlock goes to the EXISTING
// /v1/chat/sync endpoint — no new serving path. The typed question gets a
// light context prefix carrying the DDQ question + answer it follows;
// serving, gating, and verification are untouched chat/sync. v1 renders the
// result inline (session-local); because the DDQ conversation_id is passed
// through, chat/sync ALSO persists the exchange as ordinary chat messages on
// the DDQ conversation, so it survives reload as a chat bubble below the
// accordion.

export type DDQFollowup = {
  question: string;
  answer: string;
  sources: ChatSource[];
  created_at: string;
};

// Keep the context prefix light — the original answer is capped so a long
// DDQ answer can't blow up the chat prompt.
const FOLLOWUP_ANSWER_EXCERPT_CHARS = 1500;

export function buildDdqFollowupQuery(
  q: Pick<DDQAnsweredQuestion, "question_id" | "section" | "question" | "answer">,
  followupText: string,
): string {
  const answer =
    q.answer.length > FOLLOWUP_ANSWER_EXCERPT_CHARS
      ? `${q.answer.slice(0, FOLLOWUP_ANSWER_EXCERPT_CHARS)}…`
      : q.answer;
  return [
    `This is a follow-up to DDQ question ${q.question_id} (section: ${q.section}): "${q.question}"`,
    `The answer provided was:`,
    answer,
    ``,
    `Follow-up question: ${followupText.trim()}`,
  ].join("\n");
}

export async function askDdqFollowup(
  q: Pick<DDQAnsweredQuestion, "question_id" | "section" | "question" | "answer">,
  followupText: string,
  fund: string,
  conversation_id?: string | null,
): Promise<DDQFollowup> {
  const res = await postChatSync(buildDdqFollowupQuery(q, followupText), fund, conversation_id);
  return {
    question: followupText.trim(),
    answer: res.answer,
    sources: res.sources || [],
    created_at: new Date().toISOString(),
  };
}

// ─── DDQ per-question re-run (#14) ──────────────────────────────────

export type DDQRegenerateResponse = {
  submission_id: string;
  conversation_id: string;
  message_id: string;
  question: DDQAnsweredQuestion;
};

export function regenerateDdqQuestion(submissionId: string, questionId: string) {
  return request<DDQRegenerateResponse>(
    `/v1/ddq/${submissionId}/questions/${encodeURIComponent(questionId)}/regenerate`,
    { method: "POST" },
  );
}

export type DDQStreamHandlers = {
  onConversationId?: (id: string, submissionId: string, total: number) => void;
  onRetrievalComplete?: (sectionCount: number) => void;
  onQuestionComplete?: (q: DDQAnsweredQuestion) => void;
  onDone?: (info: {
    message_id: string;
    conversation_id: string;
    cancelled: boolean;
    answered: number;
    total: number;
  }) => void;
  onError?: (message: string) => void;
};

export async function streamDDQAnswer(
  submissionId: string,
  selectedQuestionIds: string[] | undefined,
  handlers: DDQStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const resp = await fetch(`/v1/ddq/${submissionId}/answer`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...impersonationHeaders() },
    body: JSON.stringify({ selected_question_ids: selectedQuestionIds }),
    signal,
  });
  if (!resp.ok || !resp.body) {
    if (resp.status === 401) handleUnauthorized();
    const text = await resp.text().catch(() => "");
    let msg = `DDQ stream failed (${resp.status})`;
    try { msg = JSON.parse(text).error || msg; } catch { /* keep */ }
    throw new Error(msg);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // Track terminal-event arrival so we know whether to fall back to
  // polling after the read loop exits. De-dupe forwarded question IDs so
  // the polling reconciliation doesn't double-deliver questions the SSE
  // stream already pushed.
  let receivedTerminalEvent = false;
  const seenQuestionIds = new Set<string>();
  const forwardQuestion = (q: DDQAnsweredQuestion) => {
    if (!q || !q.question_id) return;
    if (seenQuestionIds.has(q.question_id)) return;
    seenQuestionIds.add(q.question_id);
    handlers.onQuestionComplete?.(q);
  };

  // SSE parser: server emits typed events
  //   event: <name>\ndata: <json>\n\n
  let pendingEvent: string | null = null;
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split(/\n\n/);
      buffer = blocks.pop() ?? "";
      for (const block of blocks) {
        const lines = block.split("\n");
        pendingEvent = null;
        let payload: string | null = null;
        for (const line of lines) {
          if (line.startsWith("event: ")) pendingEvent = line.slice(7).trim();
          else if (line.startsWith("data: ")) payload = (payload || "") + line.slice(6);
        }
        if (!pendingEvent || payload == null) continue;
        let parsed: unknown;
        try { parsed = JSON.parse(payload); } catch { continue; }
        const ev = parsed as Record<string, unknown>;
        switch (pendingEvent) {
          case "conversation":
            handlers.onConversationId?.(
              String(ev.conversation_id),
              String(ev.submission_id),
              Number(ev.total),
            );
            break;
          case "retrieval_complete":
            handlers.onRetrievalComplete?.(Number(ev.section_count));
            break;
          case "question_complete":
            forwardQuestion(parsed as DDQAnsweredQuestion);
            break;
          case "done":
            receivedTerminalEvent = true;
            handlers.onDone?.({
              message_id: String(ev.message_id),
              conversation_id: String(ev.conversation_id),
              cancelled: Boolean(ev.cancelled),
              answered: Number(ev.answered),
              total: Number(ev.total),
            });
            break;
          case "error":
            receivedTerminalEvent = true;
            handlers.onError?.(String(ev.message || "Unknown error"));
            break;
        }
      }
    }
  } catch (err) {
    // Network blip, timeout, or abort. AbortError bubbles up to the
    // caller (which already handles it as a graceful cancel). For
    // anything else, fall through to the polling fallback below if
    // there's still in-flight work on the server.
    if ((err as Error)?.name === "AbortError") throw err;
  }

  if (receivedTerminalEvent) return;
  if (signal?.aborted) return;
  await pollDDQUntilTerminal(submissionId, signal, handlers, forwardQuestion);
}

/**
 * Polling fallback when the SSE stream drops before emitting a terminal
 * event. Hits /v1/ddq/:id/status every 2s for up to 5 minutes, surfaces
 * any newly-completed questions to the client, and fires onDone /
 * onError once the server reports a terminal state. De-duplication is
 * handled by `forwardQuestion` (shared with the SSE path).
 */
async function pollDDQUntilTerminal(
  submissionId: string,
  signal: AbortSignal | undefined,
  handlers: DDQStreamHandlers,
  forwardQuestion: (q: DDQAnsweredQuestion) => void,
): Promise<void> {
  const POLL_INTERVAL_MS = 2000;
  const MAX_ATTEMPTS = 150; // 5 minutes

  for (let i = 0; i < MAX_ATTEMPTS; i += 1) {
    if (signal?.aborted) return;
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    if (signal?.aborted) return;

    let status: DDQStatusResponse;
    try {
      status = await getDDQStatus(submissionId);
    } catch {
      // Transient — keep polling.
      continue;
    }

    if (status.status === "failed") {
      handlers.onError?.(status.error_message || "DDQ run failed");
      return;
    }

    if (status.status === "completed" || status.status === "cancelled") {
      const payload = status.structured?.payload;
      if (payload?.questions) {
        for (const q of payload.questions) forwardQuestion(q);
      }
      handlers.onDone?.({
        message_id: status.structured?.message_id || "",
        conversation_id: status.conversation_id || "",
        cancelled: status.status === "cancelled",
        answered: payload?.questions?.length || status.progress?.answered || 0,
        total: status.progress?.total || 0,
      });
      return;
    }
    // status === 'answering' or 'parsed' — keep polling. The status
    // endpoint doesn't surface partial question payloads, so we don't
    // forward incremental progress here; the LP just sees the last
    // SSE-delivered state until the run terminates.
  }

  handlers.onError?.("DDQ run did not complete within 5 minutes. Reload the conversation to see results.");
}

// ─── Public: invite redemption ─────────────────────────────────────

export function redeemInvite(token: string, email: string) {
  return request<{
    status: "accepted";
    email: string;
    name: string | null;
    firm: string | null;
    message: string;
  }>("/v1/invite/redeem", {
    method: "POST",
    body: JSON.stringify({ token, email }),
  });
}

// ─── DDQ citation aggregation ──────────────────────────────────────
//
// A DDQ response carries per-question `citations` arrays, each numbered
// locally (1,2,3…). The Sources panel AND the inline citation chips in the
// accordion must instead use ONE deduped, globally-numbered list so a chip's
// number matches its source card (and a repeated citation keeps the same
// number rather than double-counting). Both consumers MUST call this single
// helper on the SAME ordered `questions` array so the numbering is identical.
export function aggregateDdqSources(
  questions: ReadonlyArray<{ citations?: ChatSource[] | null }> | undefined | null,
): ChatSource[] {
  const aggregated: ChatSource[] = [];
  const seen = new Set<string>();
  let idx = 1;
  for (const q of questions ?? []) {
    for (const s of q?.citations ?? []) {
      const key = (s.tag || s.document_name || "").toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      aggregated.push({ ...s, index: idx++ });
    }
  }
  return aggregated;
}

// ─── Document Enrichment (admin) ───────────────────────────────────
// Admin adds documents on top of the seeded corpus; the agent reads each,
// surfaces its effect/conflicts vs the existing corpus, and the admin's
// steering choices become corpus directives. See docs/DOC_ENRICHMENT_SPEC.md.

export type EnrichmentSteeringChoice =
  | "lead_corroborate" | "hybridize" | "supersede" | "single_source" | "custom" | "ignore" | "accept";

export type EnrichmentCardOption = {
  choice: EnrichmentSteeringChoice;
  label: string;
  rule_text: string;
};

export type EnrichmentCard = {
  id: string;
  kind: "overlap" | "additive";
  question: string;
  answer_a: string | null;   // existing corpus
  answer_b: string | null;   // new document(s)
  sources_a: string[];
  sources_b: string[];
  divergence_type: string;
  summary: string;
  materially_differ?: boolean;
  options: EnrichmentCardOption[];
};

export type EnrichmentResolution = {
  choice: EnrichmentSteeringChoice;
  rule_text?: string;
  custom_text?: string;
};

export type EnrichmentDocSummary = {
  document_id: string;
  name: string;
  card?: { title?: string; summary?: string; suggested_tag?: string; self_sensitivity?: number };
};

export type EnrichmentStatus = {
  id: string;
  fund_id: string;
  status:
    | "staging" | "sampling" | "comparing" | "awaiting_review"
    | "publishing" | "completed" | "failed" | "cancelled";
  progress: { stage: string; total: number; done: number };
  document_ids: string[];
  doc_summaries: EnrichmentDocSummary[];
  cards: EnrichmentCard[];
  resolutions: Record<string, EnrichmentResolution>;
  error_message: string | null;
};

export type EnrichmentStreamHandlers = {
  onJob?: (jobId: string, files: number) => void;
  onStage?: (stage: string, label: string, total: number, done: number) => void;
  onProgress?: (stage: string, done: number, total: number) => void;
  onDoc?: (name: string, summary: string) => void;
  onNote?: (note: string) => void;
  onDone?: (info: { job_id: string; cards: number; overlap: number; additive: number }) => void;
  onError?: (message: string) => void;
};

/**
 * Upload + run the enrichment pipeline over a streamed SSE response. The open
 * stream keeps the worker isolate alive for the full job (Cloudflare Pages
 * drops fire-and-forget background work). Resolves when the stream ends; the
 * caller should treat onDone/onError as the terminal signals and fall back to
 * polling getEnrichmentStatus if the stream drops before either fires.
 */
export async function uploadEnrichment(
  files: File[],
  fund: string,
  handlers: EnrichmentStreamHandlers,
): Promise<void> {
  const form = new FormData();
  for (const f of files) form.append("file", f);
  form.append("fund", fund);
  const resp = await fetch("/v1/admin/enrichment/upload", {
    method: "POST",
    headers: { ...impersonationHeaders() },
    body: form,
  });
  if (!resp.ok || !resp.body) {
    const t = await resp.text();
    let p: { error?: string } = {};
    try { p = JSON.parse(t); } catch { /* keep default */ }
    if (resp.status === 401) handleUnauthorized();
    throw new Error(p.error || `Upload failed: ${resp.status}`);
  }
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() || "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const payload = line.slice(6).trim();
      if (!payload || payload === "[DONE]") continue;
      let evt: Record<string, unknown>;
      try { evt = JSON.parse(payload); } catch { continue; }
      switch (evt.type) {
        case "job": handlers.onJob?.(evt.job_id as string, evt.files as number); break;
        case "stage": handlers.onStage?.(evt.stage as string, evt.label as string, evt.total as number, evt.done as number); break;
        case "progress": handlers.onProgress?.(evt.stage as string, evt.done as number, evt.total as number); break;
        case "doc": handlers.onDoc?.(evt.name as string, (evt.summary as string) || ""); break;
        case "note": handlers.onNote?.(evt.note as string); break;
        case "done": handlers.onDone?.(evt as unknown as { job_id: string; cards: number; overlap: number; additive: number }); break;
        case "error": handlers.onError?.(evt.message as string); break;
      }
    }
  }
}

export function getEnrichmentStatus(jobId: string) {
  return request<EnrichmentStatus>(`/v1/admin/enrichment/${jobId}/status`);
}

export function resolveEnrichment(jobId: string, resolutions: Record<string, EnrichmentResolution>) {
  return request<{ ok: boolean }>(`/v1/admin/enrichment/${jobId}/resolve`, {
    method: "POST",
    body: JSON.stringify({ resolutions }),
  });
}

export function approveEnrichment(jobId: string, resolutions: Record<string, EnrichmentResolution>) {
  return request<{ ok: boolean; directives_created: number; documents_activated: number }>(
    `/v1/admin/enrichment/${jobId}/approve`,
    { method: "POST", body: JSON.stringify({ resolutions }) },
  );
}

export function cancelEnrichment(jobId: string) {
  return request<{ status: string; documents_retired: number }>(
    `/v1/admin/enrichment/${jobId}/cancel`,
    { method: "POST" },
  );
}

export type CorpusDirective = {
  id: string;
  fund_id: string | null;
  document_id: string | null;
  source_question: string | null;
  rule_text: string;
  steering_choice: string | null;
  status: string;
  created_by: string;
  created_at: string;
};

export function listDirectives(fund?: string) {
  const qs = fund ? `?fund=${encodeURIComponent(fund)}` : "";
  return request<{ directives: CorpusDirective[] }>(`/v1/admin/directives${qs}`);
}

export function retireDirective(id: string) {
  return request<{ ok: boolean; id: string }>(`/v1/admin/directives/${id}`, { method: "DELETE" });
}

// ─── Admin: WS4 registry review queue ───────────────────────────────
// WS9 certification surfacing: a row a machine certification run did NOT
// promote carries review_flags (the exception payload) and the latest gate
// results per gate (certification_checks), cell-localized so the queue can
// point at the failing CELL (expected vs computed vs suspect), not just the row.
export type CertificationGate =
  | "checksum"
  | "dual_extract"
  | "corroboration"
  | "closed_world"
  | "round_trip";

export type CertificationCheckDetails = {
  reason?: string;
  anchors?: { label?: string; type?: string; expected?: number; computed?: number | null; pass?: boolean }[];
  failing?: string[];
  suspects?: { row?: string | number; column?: string; value?: unknown; implied_value?: number | null }[];
  disagreements?: { row?: number; col?: number; a?: unknown; b?: unknown }[];
  conflicts?: { source_a?: string; source_b?: string; value_a?: unknown; value_b?: unknown }[];
  sources?: string[];
  picked?: string;
  expected?: string | number;
};

export type CertificationCheck = {
  fact_key?: string;
  gate: CertificationGate;
  result: "pass" | "fail" | "abstain" | "na";
  details: CertificationCheckDetails | null;
  run_id?: string | null;
  created_at?: string;
};

export type ReviewFlags = {
  run_id?: string;
  flagged_at?: string;
  reasons?: string[];
  checks?: { gate: CertificationGate; result: string }[];
} | null;

export type RegistryLeaseRow = {
  id: string;
  fund_id: string;
  asset_code: string;
  tenant_name: string | null;
  tenant_key: string;
  as_of_date: string;
  credit_rating: string | null;
  acres: number | null;
  ann_rev_mm: number | null;
  term_years: number | null;
  multi_site: boolean | null;
  lease_notes: string | null;
  row_status: string;
  trust: string;
  is_gated_source: boolean;
  ingest_run_id: string | null;
  created_at: string;
  review_flags?: ReviewFlags;
  certification?: CertificationCheck[];
  // sql/024 — GP integrity disposition (#59 F3); absent until the migration lands
  disposition?: string | null;
};

export function getRegistryReview(fund?: string, scope: DocAdminScope = "admin") {
  const qs = fund ? `?fund=${encodeURIComponent(fund)}` : "";
  return request<{ rows: RegistryLeaseRow[]; count: number; unnamed: number }>(
    `${adminBase(scope, "/v1/admin/registry/review", "/v1/gp/registry/review")}${qs}`,
  );
}

export function certifyRegistryRow(id: string, scope: DocAdminScope = "admin") {
  return request<{ row: RegistryLeaseRow }>(
    `${adminBase(scope, "/v1/admin/registry/review", "/v1/gp/registry/review")}/${id}/certify`,
    { method: "POST" },
  );
}

export function correctRegistryRow(
  id: string,
  tenant_name: string,
  scope: DocAdminScope = "admin",
) {
  return request<{ row: RegistryLeaseRow }>(
    `${adminBase(scope, "/v1/admin/registry/review", "/v1/gp/registry/review")}/${id}/correct`,
    {
      method: "POST",
      body: JSON.stringify({ tenant_name }),
    },
  );
}

// #59 F1: orphans now carry row identity (id) + disposition so the Review
// Queue can render them as line items and act on them. Older worker payloads
// omit the new fields — everything added is optional (extend, don't break).
export type IntegrityOrphan = {
  id?: string | null;
  fund: string;
  asset_code: string;
  tenant_name: string | null;
  disposition?: string | null;
};

export type RegistryIntegrity = {
  coverage: { fund: string; total: number; named: number; pct: number; alarm: boolean }[];
  // F2 source-of-truth for the "Coverage alarms" card (coverage[].alarm only)
  coverage_alarm_count?: number;
  orphans: IntegrityOrphan[];
  orphan_count: number;
  acknowledged_orphans?: IntegrityOrphan[];
  acknowledged_orphan_count?: number;
  page_anchor_missing: number;
  alarms: string[];
  ok: boolean;
};

export function getRegistryIntegrity(fund?: string, scope: DocAdminScope = "admin") {
  const qs = fund ? `?fund=${encodeURIComponent(fund)}` : "";
  return request<RegistryIntegrity>(
    `${adminBase(scope, "/v1/admin/registry/integrity", "/v1/gp/registry/integrity")}${qs}`,
  );
}

// #59 F3 — set/clear a GP integrity disposition on a lease row ('non_owned'
// marks a known leased-only site so its orphan finding acknowledges instead
// of alarming; null clears). GP-internal; never changes LP visibility.
export function setLeaseDisposition(
  id: string,
  disposition: string | null,
  scope: DocAdminScope = "admin",
) {
  return request<{ row: RegistryLeaseRow }>(
    `${adminBase(scope, "/v1/admin/registry/review", "/v1/gp/registry/review")}/${id}/disposition`,
    {
      method: "POST",
      body: JSON.stringify({ disposition }),
    },
  );
}

// ─── Admin: Golden Review tab (portal capture for HITL blessing) ─────────────
// Per SPEC_GOLDEN_REVIEW_TAB.md. The tab is gp_team-only (enforced at the route
// table + defense-in-depth on the decisions POST). The worker returns a snapshot
// published by bless-golden --publish-review; decisions are captured here and
// applied offline by --apply-from-db (which writes repo goldens + recomputes
// content_sha256). No LP exposure; these tables have no *_lp_safe views.

export type GoldenFactResolution = {
  statement: string;
  value: unknown;
  source_doc: string;
  source_page: number | string | null;
  status: 'on_page' | 'on_page_label' | 'other_page' | 'in_doc' | 'partial' | 'not_found' | 'manual';
  pages_found?: number[];
  snippet?: string | null;
};

export type GoldenReviewItem = {
  session_id: string;
  golden_id: number;
  section?: string | null;
  question: string;
  answer_markdown: string;
  facts: GoldenFactResolution[];
  grading: { bindings?: number; values?: number; gaps?: number; keys?: number };
  blessing: Record<string, unknown>;
  is_critical_binding: boolean;
  decision?: {
    reviewer: string;
    verdict: 'bless' | 'needs_fix';
    checks: { bindings?: boolean; values?: boolean; gaps?: boolean; keys?: boolean };
    notes?: string | null;
    page_picks?: Record<string, number | string>;
    decided_at?: string;
  } | null;
};

export type GoldenReviewSession = {
  id: string;
  golden_set: string;
  label?: string | null;
  published_by: string;
  published_at: string;
  stats: Record<string, number>;
};

export function getGoldenReview(golden?: string, scope: DocAdminScope = "admin") {
  const qs = golden ? `?golden=${encodeURIComponent(golden)}` : "";
  return request<{ session: GoldenReviewSession | null; items: GoldenReviewItem[] }>(
    `${adminBase(scope, "/v1/admin/golden/review", "/v1/gp/golden/review")}${qs}`,
  );
}

export function postGoldenDecisions(
  args: {
    reviewer: string;
    session_id?: string;
    decisions: Array<{
      golden_id: number;
      verdict: 'bless' | 'needs_fix';
      checks: { bindings?: boolean; values?: boolean; gaps?: boolean; keys?: boolean };
      notes?: string | null;
      page_picks?: Record<string, number | string>;
    }>;
  },
  scope: DocAdminScope = "admin",
) {
  return request<{ ok: true; session_id: string; count: number }>(
    `${adminBase(scope, "/v1/admin/golden/review", "/v1/gp/golden/review")}/decisions`,
    { method: 'POST', body: JSON.stringify(args) },
  );
}

// ─── Investor Golden promotion loop (Phase 1.5) ─────────────────────
//
// The flywheel's human side: a gp_team reviewer promotes Pending proposals
// (`pending_new`) and parked revisions on Approved goldens (`pending_revision`)
// into served Approved goldens. Distinct from the eval Golden Review above (that
// blesses the file-based eval set; this is the Supabase investor_goldens register).
// CCO gate is OFF — a promote serves immediately; `requires_cco` is informational.

export type GoldenProposal = {
  proposed_answer?: string;
  proposed_as_of?: string | null;
  proposed_source?: string | null;
  proposed_confidence?: string | null;
  proposed_by?: string | null;
  requires_cco?: boolean;
};

/** A gp_attested span on a proposal — institutional knowledge, represented
 *  explicitly rather than laundered into looking document-backed (#145 §1). */
export type GoldenAttestation = {
  kind: string;
  text: string;
  by: string | null;
  at: string | null;
  rationale: string | null;
};

export type GoldenQueueItem = {
  id: string;
  fund: string;
  category: string | null;
  question: string;
  status: string;
  version: number;
  kind: "pending_new" | "pending_revision";
  live_answer: string;
  live_as_of: string | null;
  proposal: GoldenProposal | null;
  requires_cco: boolean;
  proposed_by: string | null;
  /** #145 — spans a human asserted, so a reviewer can tell them from
   *  document-backed text without opening anything. */
  attestations?: GoldenAttestation[];
  /** #145 — the proposer edited the served answer body, not just appended. */
  body_edited?: boolean;
};

// ─── #145 — in-pane nuance capture ─────────────────────────────────────────

export type ProposeGoldenRequest = {
  message_id: string;
  fund: string;
  question: string;
  answer_body: string;
  body_edited: boolean;
  category?: string;
  nuance?: string;
  attestation_rationale?: string;
  as_of?: string;
  investors?: string[];
  confidence?: string;
  freshness?: string;
  vehicle_label?: string;
  bindings?: ChatBinding[];
  sources?: string[];
  accept_split?: boolean;
  expected_version?: number;
};

export type ProposeGoldenResponse = {
  golden_id: string;
  action: "created" | "updated_pending" | "proposed_revision";
  version: number;
  requires_cco: boolean;
  status: "Pending";
  feedback_id: string | null;
  note: string;
};

/**
 * The 409 the engine returns instead of a bare rejection when a cited source is
 * a gated document: an OFFER to bank the claims without the figures.
 *
 * The figure-free answer carries NO withholding marker — no "available by
 * request", no ellipsis, nothing. A visible withholding label is itself a
 * disclosure: it tells a reader that a specific number exists and is being kept
 * from them, in a row that may later be exported or synced to Notion. The
 * excluded document is recorded in the propose-time audit row only.
 */
export type GoldenSplitOffer = {
  code: "gated_source_split_available";
  error: string;
  cause: string;
  fix: string;
  gated_sources: string[];
  figure_spans: string[];
  proposed_figure_free_answer: string;
};

/** A structured governance rejection (problem → cause → fix), never a raw blob. */
export type GoldenGateRejection = {
  error: string;
  code: string;
  cause?: string;
  fix?: string;
};

export function proposeGoldenFromAnswer(
  payload: ProposeGoldenRequest,
  scope: DocAdminScope = "gp",
) {
  return request<ProposeGoldenResponse>(
    adminBase(scope, "/v1/golden/propose-from-answer", "/v1/gp/golden/propose-from-answer"),
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export type GoldenLookupCandidate = {
  id: string;
  category: string | null;
  question: string;
  similarity: number;
};

/**
 * Nearest-neighbour category prefill. BEST-EFFORT ONLY — the caller swallows
 * every failure (embedding error, RPC error, clean miss) and opens the composer
 * with a blank category. A prefill hint must never prevent a capture.
 */
export function lookupInvestorGolden(
  query: string,
  fund: string,
  scope: DocAdminScope = "gp",
) {
  const qs = `?q=${encodeURIComponent(query)}&fund=${encodeURIComponent(fund)}`;
  return request<{
    match: GoldenLookupCandidate | null;
    candidates: GoldenLookupCandidate[];
  }>(`${adminBase(scope, "/v1/golden/lookup", "/v1/gp/golden/lookup")}${qs}`);
}

export type GoldenReviewQueue = {
  count: number;
  fund: string | null;
  cco_gate: string;
  queue: GoldenQueueItem[];
};

export function getGoldenPromotionQueue(fund?: string, scope: DocAdminScope = "admin") {
  const qs = fund ? `?fund=${encodeURIComponent(fund)}` : "";
  // NOTE: the legacy path is /v1/golden/review; its gp mirror is
  // /v1/gp/golden/promotion-queue (NOT /v1/gp/golden/review — that path belongs
  // to the eval Golden Review surface). See the U4 /v1/gp/* block in index.js.
  return request<GoldenReviewQueue>(
    `${adminBase(scope, "/v1/golden/review", "/v1/gp/golden/promotion-queue")}${qs}`,
  );
}

export function promoteInvestorGolden(
  id: string,
  action: "approve" | "reject",
  opts: { note?: string; expected_version?: number } = {},
  scope: DocAdminScope = "admin",
) {
  return request<{
    id: string;
    status: string;
    version: number;
    action: string;
    kind: string;
    requires_cco: boolean;
    note: string;
  }>(adminBase(scope, "/v1/golden/promote", "/v1/gp/golden/promote"), {
    method: "POST",
    body: JSON.stringify({ id, action, ...opts }),
  });
}

// ─── Phase 3 Reporting — restatement sign-off ───────────────────────
//
// The structured-reporting ingest path (REPORTING_CONTRACT §B) stages facts,
// validates, and produces a `preview` run. The default skill path — when no
// previously-locked figure changed — promotes silently; this portal only
// surfaces a run when locked numbers were restated or a current-period figure
// changed, and a named gp_team person must acknowledge each change before the
// engine will promote it. All routes are gp_team-gated (`denyIfNotGpTeam`).

/** Where a staged value came from, for the "source" column of the diff table. */
export type ReportingChangeType =
  | "new"
  | "unchanged"
  | "restatement"
  | "changed_current";

/**
 * One row of GET /v1/reporting/runs/:id/diff. `staging_id` is the natural key
 * the /promote acknowledgement references. Exactly one of the value fields
 * carries the figure (mirrors `ck_fact_one_value`); we normalise to a display
 * string in the UI and keep the raw fields here for type-faithful rendering.
 */
export type ReportingDiffRow = {
  staging_id: string;
  entity_code: string;
  entity_name?: string | null;
  metric_key: string;
  metric_name?: string | null;
  category?: string | null;
  period_key: string;
  scenario: string;
  basis: string;
  unit?: string | null;
  currency?: string | null;
  // prior/new figures — numeric is the common case; text/date fall back to display.
  prior_value: number | string | null;
  new_value: number | string | null;
  delta?: number | null;
  pct_change?: number | null;
  change_type: ReportingChangeType;
  is_forward_looking?: boolean;
  source_sheet?: string | null;
  source_cell?: string | null;
};

export type ReportingDiffSummary = {
  run_id: string;
  rows: ReportingDiffRow[];
  // engine-computed gate (mirrors v_run_promotable); the portal also recomputes
  // client-side from acknowledgements so the button state is immediate.
  promotable?: boolean;
  // counts for the stat row, if the engine pre-aggregates them.
  counts?: Partial<Record<ReportingChangeType, number>>;
};

/**
 * Provenance metadata for an extract event: where the data came from.
 * All fields are optional — the backend may not populate this until the
 * email-ingestion path is live. Consumers must null-check every field.
 */
export type SourceMeta = {
  email_subject?: string | null;
  email_sender?: string | null;
  attachments?: string[];
  document_type?: string | null;
};

export type ReportingRunHeader = {
  run_id: string;
  template_version_id?: string | null;
  template_code?: string | null;
  platform_code?: string | null;
  source_file_name?: string | null;
  reporting_period_key?: string | null;
  status: "preview" | "committed" | "superseded";
  row_count?: number | null;
  ingested_by?: string | null;
  ingested_at?: string | null;
  notes?: string | null;
  /** Validation failures that hard-block promotion (non-empty ⇒ button must stay disabled). */
  fails?: ReportingValidationResult[];
  /** Advisory warnings (non-blocking). */
  warnings?: ReportingValidationResult[];
  /** Restatements that still need explicit acknowledgement before promote. */
  pendingRestatements?: unknown[];
  /** Extract provenance — email subject/sender/attachment (null until email-ingest path live). */
  source_meta?: SourceMeta | null;
};

export type ReportingTemplateSummary = {
  template_code: string;
  name: string;
  versions?: string[];
  latest_period?: string | null;
};

/**
 * The 422 governance-reject body. The engine returns
 * `{ error, code, cause, fix }` so the portal can render an actionable card
 * (un-acknowledged change, missing as_of, …) instead of a raw error string.
 */
export type ReportingGovernanceReject = {
  error: string;
  code?: string;
  cause?: string;
  fix?: string;
};

/** Type guard for the structured 422 payload attached to a thrown request error. */
export function isGovernanceReject(payload: unknown): payload is ReportingGovernanceReject {
  return (
    typeof payload === "object" &&
    payload !== null &&
    typeof (payload as { error?: unknown }).error === "string"
  );
}

export type ReportingRunListItem = ReportingRunHeader & {
  // when the list endpoint pre-aggregates, the portal can show "needs sign-off"
  // counts without fetching each diff. All optional — the portal recomputes
  // from the diff when these are absent.
  pending_count?: number | null;
  changed_count?: number | null;
};

/** Reporting routes are reachable two ways, like the document/ddq admin fns:
 *  the legacy sign-off portal (`/v1/reporting/*`, CF-Access-gated proxy) and the
 *  unified suite (`/v1/gp/reporting/*`, app-auth gp_team-gated proxy, NOT behind
 *  CF Access). Both forward to the same standalone investor-qa-reporting Worker —
 *  the proxy rebuilds the upstream URL from the (.*) capture, so the gp prefix is
 *  transparent (see worker/src/reporting-proxy.js). Default "admin" keeps every
 *  existing caller (admin.html / ReportingSignoffTab) byte-for-byte unchanged;
 *  the suite passes "gp". */
const reportingBase = (scope: DocAdminScope = "admin") =>
  scope === "gp" ? "/v1/gp/reporting" : "/v1/reporting";

export function getReportingTemplates(scope: DocAdminScope = "admin") {
  return request<{ templates: ReportingTemplateSummary[] }>(
    `${reportingBase(scope)}/templates`,
  );
}

/**
 * Best-effort list of runs awaiting sign-off. The frozen contract (§B) defines
 * the per-run routes but not a collection route; the ingest path hands the
 * portal a `run_id` directly. This call targets `GET /v1/reporting/runs?status=preview`
 * and degrades to an empty list if the engine has not exposed it yet (404/501),
 * so the portal never hard-fails on the missing list — the operator can always
 * paste/open a run by id. A real auth failure (401) still bounces to /login,
 * and any other unexpected error is rethrown so it surfaces in the UI banner.
 */
export async function listPreviewReportingRuns(
  scope: DocAdminScope = "admin",
): Promise<ReportingRunListItem[]> {
  try {
    const r = await request<{ runs: ReportingRunListItem[] }>(
      `${reportingBase(scope)}/runs?status=preview`,
    );
    return r.runs ?? [];
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status === 404 || status === 501) return [];
    throw err;
  }
}

export async function getReportingRun(
  runId: string,
  scope: DocAdminScope = "admin",
): Promise<ReportingRunHeader> {
  // The worker returns the header nested under `run` and spreads the run
  // summary (promotable/fails/warnings/pendingRestatements) at the top level:
  //   { run: {…status, template_code, reporting_period_key}, fails, warnings, … }
  // Flatten so callers read `header.status` etc. directly (both the suite view
  // and the legacy tab do). Tolerate an already-flat shape defensively.
  const res = await request<
    ReportingRunHeader & {
      run?: ReportingRunHeader;
      fails?: ReportingRunHeader["fails"];
      warnings?: ReportingRunHeader["warnings"];
      pendingRestatements?: ReportingRunHeader["pendingRestatements"];
    }
  >(`${reportingBase(scope)}/runs/${encodeURIComponent(runId)}`);
  if (res && typeof res === "object" && res.run) {
    const { run, ...summary } = res;
    return {
      ...run,
      fails: summary.fails ?? run.fails,
      warnings: summary.warnings ?? run.warnings,
      pendingRestatements: summary.pendingRestatements ?? run.pendingRestatements,
    };
  }
  return res;
}

export function getReportingDiff(runId: string, scope: DocAdminScope = "admin") {
  return request<ReportingDiffSummary>(
    `${reportingBase(scope)}/runs/${encodeURIComponent(runId)}/diff`,
  );
}

/**
 * POST /v1/reporting/runs/:id/promote. Body matches the contract exactly:
 * `{ acknowledged_by, acknowledgements: [{ staging_id, note }] }`. On a
 * governance reject the engine answers 422; `request()` throws an Error whose
 * `.status` is 422 and `.payload` is the {error,cause,fix} body.
 */
export function promoteReportingRun(
  runId: string,
  body: {
    acknowledged_by: string;
    acknowledgements: Array<{ staging_id: string; note?: string }>;
  },
  scope: DocAdminScope = "admin",
) {
  return request<{ run_id: string; status: "committed"; promoted: number }>(
    `${reportingBase(scope)}/runs/${encodeURIComponent(runId)}/promote`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/* ── Metric alias registry (field→metric symlinks; Tech Fund vault) ───────────
   Human-blessed mappings from portco-specific field names onto standard
   metric_keys. Proposals land on vault-ingest; Sign-off reviews them. */

export type MetricAlias = {
  alias_id: number;
  platform_code: string;
  entity_code: string;
  source_field: string;
  source_label_raw?: string | null;
  metric_key?: string | null;
  scale_multiplier: number;
  unit_hint?: string | null;
  status: "proposed" | "blessed" | "rejected" | "superseded";
  confidence?: number | null;
  proposed_by?: string | null;
  blessed_by?: string | null;
  rationale?: string | null;
  proposed_run_id?: number | null;
  proposed_raw?: string | null;
  proposed_currency?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type MetricCatalogEntry = {
  metric_key: string;
  name: string;
  category: string;
  value_kind: string;
  unit_code: string;
};

/** GET /v1/gp/reporting/runs/:id/mapping-proposals */
export function getRunMappingProposals(
  runId: string,
  scope: DocAdminScope = "gp",
) {
  return request<{
    run_id: number;
    platform_code: string;
    entity_code: string | null;
    proposals: MetricAlias[];
    count: number;
  }>(
    `${reportingBase(scope)}/runs/${encodeURIComponent(runId)}/mapping-proposals`,
  );
}

/** GET /v1/gp/reporting/metric-catalog */
export function getMetricCatalog(scope: DocAdminScope = "gp") {
  return request<{ metrics: MetricCatalogEntry[]; count: number }>(
    `${reportingBase(scope)}/metric-catalog`,
  );
}

/**
 * POST /v1/gp/reporting/runs/:id/suggest-mappings — reasoning model pre-fills a
 * suggested metric_key + confidence + rationale on this run's unmapped proposals.
 * Never blesses (rows stay `proposed`); the human still confirms in the panel.
 */
export function suggestRunMappings(runId: string, scope: DocAdminScope = "gp") {
  return request<{
    run_id: number;
    entity_code: string | null;
    model: string;
    message?: string;
    suggested: Array<{
      source_field: string;
      metric_key: string | null;
      confidence?: number | null;
      rationale?: string | null;
    }>;
    count: number;
  }>(`${reportingBase(scope)}/runs/${encodeURIComponent(runId)}/suggest-mappings`, {
    method: "POST",
    body: "{}",
  });
}

/** POST /v1/gp/reporting/aliases/bless — restages + auto-promotes when gate allows. */
export function blessMetricAlias(
  body: {
    alias_id: number;
    metric_key?: string | null;
    keep_as_extra?: boolean;
    scale_multiplier?: number;
    unit_hint?: string | null;
    restage?: boolean;
  },
  scope: DocAdminScope = "gp",
) {
  return request<{
    alias: MetricAlias;
    restage: {
      skipped: boolean;
      staged?: boolean;
      promoted?: boolean;
      promotable?: boolean;
      run_id?: number;
      reason?: string;
    };
  }>(`${reportingBase(scope)}/aliases/bless`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** POST /v1/gp/reporting/aliases/reject */
export function rejectMetricAlias(
  body: { alias_id: number; note?: string },
  scope: DocAdminScope = "gp",
) {
  return request<{ alias: MetricAlias }>(
    `${reportingBase(scope)}/aliases/reject`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/**
 * GET /v1/gp/reporting/aliases — list metric-alias rows across ALL entities
 * (entity omitted = global). Powers the Portfolio ▸ Data "Field Mappings"
 * surface: one place to see every pending field→metric proposal awaiting a
 * human bless, instead of per-run-buried in Reporting Sign-off.
 */
export function listMetricAliases(
  opts: { status?: MetricAlias["status"]; entity?: string } = {},
  scope: DocAdminScope = "gp",
) {
  const qs = new URLSearchParams();
  if (opts.status) qs.set("status", opts.status);
  if (opts.entity) qs.set("entity", opts.entity);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<{ aliases: MetricAlias[]; count: number }>(
    `${reportingBase(scope)}/aliases${suffix}`,
  );
}

/* ── Upload → stage → AI spot-check → attest (Angus, 2026-07-02) ──────────────
   The Portfolio ▸ Upload & attest flow. An analyst uploads a quarterly pack; we
   stage it as a PREVIEW run (validation + diff), run an ADVISORY AI spot-check,
   then a human attests via the same promoteReportingRun() sign-off path. The AI
   never promotes — acknowledged_by is stamped from the authenticated human and
   FAILs / un-acknowledged restatements block promote (422). */

/** One deterministic validation_result row (readRunSummary shape). */
export type ReportingValidationResult = {
  check_code: string;
  status: string;
  detail?: string | null;
  observed?: number | string | null;
  expected?: number | string | null;
  variance?: number | string | null;
};

/** The staging response from POST /v1/reporting/ingest (handleReportingIngest). */
export type ReportingIngestResult = {
  run_id: string | number;
  status: string;
  input_rows?: number;
  staged?: number;
  resolved?: number;
  unresolved?: number;
  unmapped?: number;
  /** Rows the template deliberately skips (`template_version.ignore_row_codes`) —
   *  derived subtotals like the asset tab's "Total / WA". Counted apart from
   *  unmapped/unresolved so an intentional exclusion never reads as a parse failure. */
  ignored?: number;
  ignored_row_codes?: string[];
  promotable?: boolean;
  fails?: ReportingValidationResult[];
  warnings?: ReportingValidationResult[];
  pending_restatements?: unknown[];
  /** Present when nothing staged / nothing resolved / the read was partial — a
   *  self-explaining reason naming the actual mismatch. */
  hint?: string;
  /**
   * Which mapped row labels produced no fact, and why. A staged count alone cannot
   * distinguish "the platform didn't report it" from "we failed to read it" — the
   * Outpost Q2-2026 pack staged 62 rows while the entire income statement was
   * silently missing (2026-08-11). `row_not_found` is the actionable bucket: the
   * source either dropped the block or reworded the label.
   */
  coverage?: {
    /** Every expected map target across BOTH layouts (rows + transposed columns). */
    expected_targets: number;
    matched_targets: number;
    /** Row IS on the sheet but carried no value — a reporting gap, expected. */
    present_but_no_value: string[];
    /** Row is not on the sheet at all — dropped block or reworded label. */
    row_not_found: string[];
    /**
     * The TRANSPOSED (asset-tab) half. Present only when the template's map is keyed by
     * metric COLUMN — the asset tab puts the metric labels in the header and the asset
     * code in the row, so the two buckets above (keyed by row label) had nothing to say
     * about it and the whole asset book went unreported until 2026-08-14.
     *
     * Reported on the axis that makes each signal actionable rather than as an 18×41 grid:
     * the metric axis (a column absent from the header costs EVERY asset that metric), the
     * asset axis (an asset that produced nothing at all), and one capped per-cell bucket.
     */
    transposed?: {
      layout: "label_columns";
      columns_expected: number;
      columns_matched: number;
      /** Mapped metric column missing from the header — renamed or dropped. Act on it. */
      column_not_found: string[];
      /** Header is present but no asset reported a value — a source-side gap. */
      column_present_but_no_value: string[];
      /** Asset rows the sheet listed, less the ones the template deliberately skips. */
      rows_seen: number;
      rows_with_facts: number;
      /** Asset rows that produced no fact at all (capped). */
      rows_with_no_facts: string[];
      cells_expected: number;
      cells_with_facts: number;
      /** Columns read for SOME assets but not all — widest gap first, capped. */
      partial_columns: Array<{ column: string; missing: number; row_codes: string[] }>;
    };
  };
  /** Bounded sample of parsed cells that matched no map row. */
  unmapped_samples?: Array<{
    row_code: string | null;
    column_header: string | null;
    source_cell: string | null;
    raw_value: string | null;
  }>;
  /** How the workbook was read: tabs found, which mapped sheet each bound to, and
   *  the period-header rows detected in each. Absent on the pre-parsed `rows` path. */
  parse?: {
    workbook_sheets?: Array<{ name: string; rows: number }>;
    map_sheets?: string[];
    sheet_binding?: Array<{ workbook_sheet: string; map_sheet: string }>;
    sheet_binding_rule?: "matched" | "sole_sheet_fallback" | "none";
    sheets?: Array<{
      name: string;
      bound_to: string | null;
      map_rows_in_scope: number;
      header_rows: Array<{ excel_row: number; mode: string; headers: string[] }>;
    }>;
  };
};

/**
 * POST /v1/gp/reporting/ingest — stage an uploaded workbook as a PREVIEW run.
 * `workbook_base64` is the raw .xlsx bytes, base64-encoded (no data: prefix). The
 * worker resolves cells against the template's column map, runs validation, and
 * returns the staging telemetry above. period_key must be a seeded period.
 */
export function ingestReportingWorkbook(
  body: {
    template_code: string;
    period_key: string;
    platform: string;
    workbook_base64: string;
    source_file_name?: string;
    notes?: string;
  },
  scope: DocAdminScope = "gp",
) {
  return request<ReportingIngestResult>(`${reportingBase(scope)}/ingest`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type ReportingSpotCheckSeverity = "info" | "warning" | "critical";

export type ReportingSpotCheckFinding = {
  severity: ReportingSpotCheckSeverity;
  title: string;
  detail: string;
  entity_code?: string | null;
  metric_key?: string | null;
  period_key?: string | null;
};

/** Advisory AI spot-check payload from POST /v1/gp/reporting/runs/:id/spot-check.
 *  Deterministic heuristics + validation are ALWAYS present; the AI narrative is
 *  present unless `degraded` (model unavailable) — the human review is never
 *  blocked by the advisory layer. */
export type ReportingSpotCheck = {
  run_id: string;
  run: {
    run_id: string;
    template_code: string | null;
    platform_code: string | null;
    period_key: string | null;
    source_file_name: string | null;
    status: string | null;
    ingested_by: string | null;
  };
  validation: {
    fails: ReportingValidationResult[];
    warnings: ReportingValidationResult[];
    fail_count: number;
    warning_count: number;
  };
  heuristics: {
    counts: Record<string, number>;
    sign_flips: Array<{
      entity_code: string;
      metric_key: string;
      period_key: string;
      prior: number;
      next: number;
    }>;
    big_swings: Array<{
      entity_code: string;
      metric_key: string;
      period_key: string;
      prior: number;
      next: number;
      pct_change: number;
      change_type: string;
    }>;
  };
  diff_rows_total: number;
  diff_rows_considered: number;
  truncated: boolean;
  promotable: boolean | null;
  risk_rating: "low" | "medium" | "high" | "unknown";
  summary: string;
  recommended_action: string;
  findings: ReportingSpotCheckFinding[];
  degraded: boolean;
  degraded_reason?: string;
  model?: string;
  generated_at?: string;
};

/**
 * POST /v1/gp/reporting/runs/:id/spot-check — advisory AI review of a staged run.
 * Handled by the MAIN worker (it holds the model key), which reads the run's diff
 * + validation from the reporting-worker and narrates. ADVISORY ONLY — it never
 * promotes or mutates a fact.
 */
export function spotCheckReportingRun(runId: string, scope: DocAdminScope = "gp") {
  return request<ReportingSpotCheck>(
    `${reportingBase(scope)}/runs/${encodeURIComponent(runId)}/spot-check`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

/* ════════════════════════════════════════════════════════════════════════
   PORTFOLIO — reporting reads for the GP-facing Portfolio product.

   All four fns hit the SAME standalone investor-qa-reporting Worker through
   the existing `/v1/gp/reporting/*` app-auth proxy (reportingBase("gp")); the
   suite is NOT behind CF Access. They default to scope "gp" (the legacy admin
   portal never calls these). Shapes below were verified against the worker
   source:
     - facts:    reporting-worker/src/reporting.js handleReportingFacts /
                 shapeFactRow / FACT_COLUMNS / renderRows (json → {rows,count}).
     - timeseries: reporting.js handleReportingTimeseries — IMPORTANT: it
                 reuses the SAME fact projection (FACT_COLUMNS) ordered by
                 period_end and returns the SAME {rows,count} envelope. There is
                 NO {points:[…]} shape; the timeseries IS a list of facts. We
                 expose `points` as an alias of `rows` for ergonomic charting.
     - overview: reporting-worker/src/techfund-read.js handleTechfundOverview —
                 {source, platform, fund, companies, staleness, fund_as_of?}.
                 Per-company fields are `name`/`active`/`days_stale`/`is_stale`/
                 `fmv_usd`/`cost_basis_usd` (NOT entity_name/is_active/fmv/cost/
                 staleness_days as the scaffold brief assumed) — typed to match.
   Every field is optional / permissive: the endpoints exist but may return an
   empty `rows`/`companies` until data lands.
   ════════════════════════════════════════════════════════════════════════ */

/** One promoted, provenance-complete fact (REPORTING_CONTRACT §B / FACT_COLUMNS).
 *  The worker hard-skips any row missing `acknowledged_by` + `as_of`, so a served
 *  fact always carries a named signer and an as-of date. */
export type ReportingFact = {
  platform: string;
  entity_code: string;
  /** Present in FACT_COLUMNS though not in the original scaffold list. */
  entity_name?: string | null;
  metric_key: string;
  /** Human label + category are emitted by the worker; tolerate absence. */
  metric_name?: string | null;
  category?: string | null;
  period_key: string;
  scenario: string;
  basis: string;
  /** Typed value: numeric | text | date (coalesced server-side). */
  value: number | string | null;
  unit: string | null;
  currency: string | null;
  // PG numeric columns serialize as STRINGS over JSON (node-postgres default,
  // to preserve precision) — both `value` and `usd_value` arrive as e.g.
  // "4610282.25", not numbers. Consumers must Number()-coerce.
  usd_value: number | string | null;
  is_forward_looking: boolean | null;
  as_of: string | null;
  source_sheet?: string | null;
  source_cell?: string | null;
  acknowledged_by: string | null;
  ingested_at: string | null;
};

export type ReportingFactsParams = {
  platform?: string;
  entity?: string;
  metric?: string;
  period?: string;
  scenario?: string;
  basis?: string;
};

/** Append only the provided filters to a URLSearchParams (skip blanks). */
function appendReportingParams(
  qs: URLSearchParams,
  params: Record<string, string | undefined | null>,
) {
  for (const [k, v] of Object.entries(params)) {
    if (v != null && String(v).trim() !== "") qs.set(k, String(v).trim());
  }
}

/**
 * GET /v1/gp/reporting/facts?…&format=json → {rows: ReportingFact[], count}.
 * Promoted + provenance-complete facts only (the worker filters un-signed /
 * un-dated rows). All filters optional.
 */
export function getReportingFacts(
  params: ReportingFactsParams = {},
  scope: DocAdminScope = "gp",
) {
  const qs = new URLSearchParams();
  appendReportingParams(qs, params);
  qs.set("format", "json");
  return request<{ rows: ReportingFact[]; count: number; format?: string }>(
    `${reportingBase(scope)}/facts?${qs.toString()}`,
  );
}

/** Per-company headline + valuation row from the tech-fund overview rollup. */
export type TechfundCompany = {
  entity_code: string;
  /** Display name (worker emits `name`, not `entity_name`). */
  name: string | null;
  /** false ⇒ realized position, excluded from the live `fund` rollup. */
  active: boolean | null;
  /** Parent vehicle code/name (REITV | SELECT_FUND_I | SELECT_FUND_P | BT_SPV). */
  vehicle_code?: string | null;
  vehicle_name?: string | null;
  latest_period: string | null;
  latest_period_end: string | null;
  /** Days since the company's latest reported period_end (H6 staleness). */
  days_stale: number | null;
  /** true when days_stale exceeds the worker's staleness threshold. */
  is_stale: boolean | null;
  /** e.g. "arr" | "revenue_ltm" | "mrr_annualized" (MRR is x12-annualized). */
  headline_metric: string | null;
  headline_value: number | string | null;
  headline_usd: number | null;
  headline_currency: string | null;
  fmv_usd: number | null;
  cost_basis_usd: number | null;
  moic: number | null;
  headcount: number | string | null;
  confidence: number | null;
  narrative: string | null;
  fmv_as_of: string | null;
};

/** Live-book fund rollup (ACTIVE positions only) from handleTechfundOverview. */
export type TechfundFundRollup = {
  basis: string;
  total_fmv_usd: number;
  total_cost_usd: number;
  moic: number | null;
  active_positions: number;
  companies_count: number;
};

/** Point-in-time EY tie-out rollup — only present when `as_of` is passed. */
export type TechfundFundAsOf = {
  basis: string;
  as_of_period: string;
  total_fmv_usd: number;
  positions_count: number;
  includes_realized: boolean;
  positions: Array<{
    entity_code: string;
    name: string | null;
    active: boolean | null;
    fmv_usd: number | null;
  }>;
};

/** Per-vehicle summary over a single TECH_FUND vehicle (`vehicles[]` entry).
 *  `total_fmv_as_of` is only present when `asOf` was passed. */
export type TechfundVehicleSummary = {
  vehicle_code: string | null;
  vehicle_name: string | null;
  /** From the vehicle's attributes (e.g. "Select Fund I", "BT SPV"). */
  display_name: string | null;
  /** "co_invest" | "spv" | null (the primary ReITV fund has none). */
  fund_type: string | null;
  total_fmv_usd: number;
  total_cost_usd: number;
  moic: number | null;
  active_positions: number;
  companies_count: number;
  /** Point-in-time (incl. realized) total; only present when `asOf` was passed. */
  total_fmv_as_of?: number;
};

/** All-vehicles program rollup (`program`). */
export type TechfundProgramRollup = {
  total_fmv_usd: number;
  total_cost_usd: number;
  moic: number | null;
  active_positions: number;
  companies_count: number;
  /** Point-in-time program total; only present when `asOf` was passed. */
  total_fmv_as_of?: number;
};

export type TechfundOverview = {
  source?: string;
  platform?: string;
  /** The vehicle the primary `fund`/`companies`/`staleness` are scoped to
   *  (defaults to "REITV"; "ALL"/"PROGRAM" = the whole program). */
  vehicle?: string;
  fund: TechfundFundRollup;
  companies: TechfundCompany[];
  staleness?: {
    threshold_days: number;
    max_period_end: string | null;
    stale_count: number;
    stale_companies: Array<{
      entity_code: string;
      latest_period: string | null;
      days_stale: number | null;
    }>;
  };
  /** Per-vehicle summaries over ALL vehicles (ReITV first). Always present. */
  vehicles?: TechfundVehicleSummary[];
  /** The all-vehicles program rollup. Always present. */
  program?: TechfundProgramRollup;
  /** Present only when `asOf` (YYYY-MM) was passed. */
  fund_as_of?: TechfundFundAsOf;
};

/**
 * GET /v1/gp/reporting/techfund/overview[?as_of=YYYY-MM][&vehicle=CODE]. Omit
 * `asOf` for the live book; pass `YYYY-MM` to additionally get the point-in-time
 * EY tie-out (`fund_as_of`). The worker 400s on a malformed as_of/vehicle, so we
 * only forward well-formed values. `vehicle` defaults (server-side) to REITV; pass
 * a vehicle code to re-scope, or "ALL" for the program rollup. The response always
 * carries `vehicles[]` + `program` regardless of scope.
 */
export function getTechfundOverview(
  asOf?: string,
  scope: DocAdminScope = "gp",
  vehicle?: string,
) {
  const qs = new URLSearchParams();
  if (asOf && /^\d{4}-\d{2}$/.test(asOf.trim())) qs.set("as_of", asOf.trim());
  if (vehicle && /^[A-Za-z_]+$/.test(vehicle.trim())) {
    qs.set("vehicle", vehicle.trim().toUpperCase());
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<TechfundOverview>(
    `${reportingBase(scope)}/techfund/overview${suffix}`,
  );
}

/**
 * One fact row from GET /v1/gp/reporting/techfund/facts?entity=<CODE>.
 * Shaped by shapeTfRow in reporting-worker/src/techfund-read.js.
 * Unlike the generic ReportingFact, every period is included (no ack-gate);
 * confidence and usd_value are the quality signals surfaced instead.
 */
export type TechfundFact = {
  entity_code: string;
  entity_name: string | null;
  metric_key: string;
  metric_name: string | null;
  category: string | null;
  period_key: string;
  scenario: string | null;
  basis: string | null;
  /** Coalesced value: numeric | text | date (server-side). PG numerics arrive as
   *  strings over JSON — callers must Number()-coerce when numeric. */
  value: number | string | null;
  unit: string | null;
  currency: string | null;
  usd_value: number | string | null;
  confidence: number | string | null;
  is_forward_looking: boolean | null;
  /** period_end date (YYYY-MM-DD) — the "as of" for this fact. */
  as_of: string | null;
  acknowledged_by: string | null;
  ingested_at: string | null;
  /** Extract provenance — email subject/attachment/document_type (null until email-ingest path live). */
  source_meta?: SourceMeta | null;
};

/**
 * GET /v1/gp/reporting/techfund/facts?entity=<CODE>[&metric=…][&period=…]
 *
 * Returns every fact across ALL periods for a company (or filtered by metric /
 * period). The endpoint is already proxied by the existing /v1/gp/reporting/(.*)
 * catch-all in the Pages worker — no new proxy route needed.
 *
 * The response envelope is { source, platform, rows, count }.
 */
export function getTechfundFacts(
  entityCode: string,
  scope: DocAdminScope = "gp",
  opts: { metric?: string; period?: string } = {},
) {
  const qs = new URLSearchParams();
  qs.set("entity", entityCode.toUpperCase());
  if (opts.metric) qs.set("metric", opts.metric);
  if (opts.period) qs.set("period", opts.period);
  qs.set("format", "json");
  return request<{ source: string; platform: string; rows: TechfundFact[]; count: number }>(
    `${reportingBase(scope)}/techfund/facts?${qs.toString()}`,
  );
}

/* ─── Tech Fund cohort (Deep Analysis feed) ──────────────────────────────────
 * GET /v1/gp/reporting/techfund/cohort — the seven-position revenue cohort COMPUTED
 * in the reporting Worker from store facts (monthly vault extracts + the promoted
 * historical metrics book). Nothing here is typed in; every point carries the fact it
 * came from. Shaped by reporting-worker/src/techfund-cohort.js buildTechfundCohort. */

export type TechfundCohortPointSource = "entry" | "vault" | "book";

export type TechfundCohortPoint = {
  /** Period end (YYYY-MM-DD). */
  date: string;
  yearsSinceEntry: number;
  /** Native value × the cohort's single fixed rate for its currency, in USD millions. */
  revenueUsdM: number;
  /** revenueUsdM ÷ the company's entry revenue (1 at entry). */
  index: number | null;
  /** Native full-unit value and its currency as stored. */
  nativeValue: number;
  currency: string;
  source: TechfundCohortPointSource;
  metricKey: string;
  basis: string | null;
  variant: string | null;
  periodKey: string | null;
  acknowledgedBy: string | null;
  confidence: number | null;
  verification: string | null;
  /** Admitted after individual source review; raw extraction status stays separate. */
  cohortSourceReviewed?: boolean;
  ref: { runId?: number | string | null; rawObservationId?: number | string | null; seriesId?: number | string | null };
};

export type TechfundCohortFlag = {
  code: string;
  severity: "resolve" | "note";
  message: string;
  date?: string;
};

export type TechfundCohortPosition = {
  costUsd: number | null;
  fmvUsd: number | null;
  moic: number | null;
  markPeriod: string | null;
  method: string | null;
  latestReportingPeriod: string | null;
};

export type TechfundCohortCompany = {
  key: string;
  entityCode: string;
  name: string;
  description: string;
  basis: string;
  metricKey: string;
  investedUsdM: number | null;
  entryDate: string;
  entryRevenueUsdM: number;
  latestDate: string;
  latestRevenueUsdM: number;
  growthEligible: boolean;
  yearsHeld: number;
  multiple: number | null;
  cagr: number | null;
  series: TechfundCohortPoint[];
  entry: { source: "vault"; acknowledgedBy: string | null; currency: string };
  position: TechfundCohortPosition;
  flags: TechfundCohortFlag[];
};

export type TechfundCohortExcluded = {
  key: string;
  entityCode: string;
  name: string;
  reason: "entry_missing" | "no_observations_after_entry" | "fx_unavailable" | string;
  message: string;
};

export type TechfundCohort = {
  source: string;
  platform: string;
  cohort: { key: string; methodVersion: string; label: string; scope: string; memberCount: number };
  asOf: string;
  generatedAt: string;
  fx: { mode: "fixed_rate_per_currency"; rates: Record<string, { rate: number; asOfDate: string; source: string }> };
  companies: TechfundCohortCompany[];
  capitalStructure?: Array<{
    key: string; entityCode: string; name: string; status: "reference" | "unavailable"; reason: string | null;
    preferenceUsdM: number | null; equityReferenceUsdM: number | null;
    preferenceRevenueMultiple: number | null; equityRevenueMultiple: number | null;
    referenceDate: string | null; revenueDate: string | null; currency: string;
    sourceDocument: string | null; sourceSheet: string | null; sourceRunId: number | string | null;
  }>;
  positions: Array<{key: string; entityCode: string; name: string; position: TechfundCohortPosition}>;
  excluded: TechfundCohortExcluded[];
  aggregate: {
    investedUsdM: number | null;
    entryRevenueUsdM: number | null;
    latestRevenueUsdM: number | null;
    revenueMultiple: number | null;
    weightedDurationYears: number | null;
    investedCapitalWeightedCagr: number | null;
    revenueWeightedDurationYears: number | null;
    revenueWeightedCagr: number | null;
    simpleDurationYears: number | null;
    simpleCagr: number | null;
    lowest: { key: string; name: string; multiple: number } | null;
    highest: { key: string; name: string; multiple: number } | null;
    companyCount: number;
    comparisonCount: number;
  };
  positionsTotal: { costUsd: number; fmvUsd: number; moic: number | null; count: number };
  caveats: string[];
};

export function getTechfundCohort(scope: DocAdminScope = "gp") {
  return request<TechfundCohort>(`${reportingBase(scope)}/techfund/cohort`);
}

// ─── Tech Fund ▸ Forward value (docs/TECHFUND_FORWARD_VALUE_BUILD_PLAN.md §7) ───
// Reads go through the /v1/gp/reporting/ catch-all; writes go to the Pages Worker's
// signed handlers under /v1/gp/portfolio/techfund/forecast/ (forecast-proxy.js),
// which add the write capability and the actor assertion. Engine types come from
// the shared module, so screen, export and snapshot speak one shape.

export type {
  EngineInput as ForecastEngineInput,
  EngineOutput as ForecastEngineOutput,
} from "../../../shared/forecast/engine.js";
export type {
  ForecastCase,
  ForecastGroup,
  ForecastSettings,
  Scenario as ForecastScenario,
} from "../../../shared/forecast/schema.js";

export type ForecastSetStatus = "draft" | "published" | "archived";

export type ForecastSetMeta = {
  setId: string;
  name: string;
  status: ForecastSetStatus;
  revision: number;
  isCurrent: boolean;
  basedOnSetId: string | null;
  createdBy: string;
  createdAt: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  publishedBy: string | null;
  publishedAt: string | null;
  publishNote: string | null;
  archivedBy: string | null;
  archivedAt: string | null;
};

export type ForecastBundle = {
  /** "snapshot": a published set, immutable; "live": a draft computed from today's store. */
  source: "snapshot" | "live";
  set: ForecastSetMeta;
  snapshot: {
    revision: number;
    engineVersion: string;
    inputsSha256: string;
    outputsSha256: string;
    createdBy: string;
    createdAt: string | null;
  } | null;
  inputs: import("../../../shared/forecast/engine.js").EngineInput & { provenance?: unknown };
  outputs: import("../../../shared/forecast/engine.js").EngineOutput;
};

export type ForwardValueAccess = {
  /** The Forward value tab is on for this person (gp_team, flag on, not previewing as an LP). */
  enabled: boolean;
  /** This deployment can sign set edits for this person (spec §8.2). Implies `enabled`. */
  writable: boolean;
  /** The signed-in email from /v1/me (present only while `enabled`): create reconciliation matches `createdBy` against it (spec F1). */
  email?: string;
};

/** Read /v1/me once for the Forward value flags. Fails closed: anything but an explicit `true` is off. */
export async function getForwardValueAccess(): Promise<ForwardValueAccess> {
  try {
    const me = await request<Me>("/v1/me");
    const enabled = me.forward_value_enabled === true;
    const access: ForwardValueAccess = { enabled, writable: enabled && me.forward_value_writable === true && me.impersonating !== true };
    if (enabled && typeof me.email === "string" && me.email.trim()) access.email = me.email.trim();
    return access;
  } catch {
    return { enabled: false, writable: false };
  }
}

/** Options for the forecast calls: the set editor passes an AbortSignal for its 30-second timeout. */
export type ForecastCallOptions = { signal?: AbortSignal };

/** `which`: a set id, "current" (the published current set) or "draft" (the latest draft). */
export function getForecastBundle(which: string = "current", opts: ForecastCallOptions = {}) {
  return request<ForecastBundle>(`${reportingBase("gp")}/techfund/forecast?set=${encodeURIComponent(which)}`, { signal: opts.signal });
}

/**
 * GET sets (set-editor spec §8.2, WP1b). `viewer` is a UI hint from the reporting
 * Worker's allowlists (it authorizes nothing; `email` is null when no user was
 * forwarded, never the service account); `seed` is the read-only create seed
 * (REITV_SEED_SETTINGS). Both are absent on a Worker from before Phase B (B7).
 */
export type ForecastSetViewer = { email: string | null; canEdit: boolean; canPublish: boolean };
export type ForecastSetList = {
  sets: ForecastSetMeta[];
  viewer?: ForecastSetViewer;
  seed?: Partial<import("../../../shared/forecast/schema.js").ForecastSettings>;
};

export function listForecastSets(opts: ForecastCallOptions = {}) {
  return request<ForecastSetList>(`${reportingBase("gp")}/techfund/forecast/sets`, { signal: opts.signal });
}

const FORECAST_WRITE_BASE = "/v1/gp/portfolio/techfund/forecast/sets";

export function createForecastSet(body: { name: string; basedOnSetId?: string | null }, opts: ForecastCallOptions = {}) {
  return request<{ set: ForecastSetMeta }>(FORECAST_WRITE_BASE, { method: "POST", body: JSON.stringify(body), signal: opts.signal });
}

export function saveForecastDraft(
  setId: string,
  body: {
    expectedRevision: number;
    name?: string;
    settings: import("../../../shared/forecast/schema.js").ForecastSettings;
    groups: import("../../../shared/forecast/schema.js").ForecastGroup[];
    cases: import("../../../shared/forecast/schema.js").ForecastCase[];
  },
  opts: ForecastCallOptions = {},
) {
  return request<{ set: ForecastSetMeta }>(`${FORECAST_WRITE_BASE}/${encodeURIComponent(setId)}/draft`, { method: "POST", body: JSON.stringify(body), signal: opts.signal });
}

export function publishForecastSet(setId: string, body: { expectedRevision: number; publishNote: string; makeCurrent?: boolean }, opts: ForecastCallOptions = {}) {
  return request<{ set: ForecastSetMeta; snapshot: { inputsSha256: string; outputsSha256: string; engineVersion: string } }>(
    `${FORECAST_WRITE_BASE}/${encodeURIComponent(setId)}/publish`, { method: "POST", body: JSON.stringify(body), signal: opts.signal });
}

export function archiveForecastSet(setId: string, body: { expectedRevision?: number } = {}, opts: ForecastCallOptions = {}) {
  return request<{ set: ForecastSetMeta }>(`${FORECAST_WRITE_BASE}/${encodeURIComponent(setId)}/archive`, { method: "POST", body: JSON.stringify(body), signal: opts.signal });
}

// ─── Managed Historical analytics ─────────────────────────────────

export type HistoricalMetricCoverage = {
  metric_key: string;
  metric_name: string | null;
  category: string | null;
  fact_count: number;
  entity_count: number;
  earliest: string | null;
  latest: string | null;
  bases: string[];
  units: string[];
};

export type HistoricalCoverageResponse = {
  source: string;
  platform: "TECH_FUND";
  scenario: string;
  include_future: boolean;
  summary: {
    fact_count: number;
    entity_count: number;
    metric_count: number;
    earliest: string | null;
    latest: string | null;
  };
  metrics: HistoricalMetricCoverage[];
};

export type HistoricalMetricPoint = {
  entity_code: string;
  entity_name: string;
  metric_key: string;
  metric_name: string | null;
  category: string | null;
  period_key: string;
  period_start: string | null;
  as_of: string;
  scenario: string;
  basis: string;
  value: number | string | null;
  unit: string | null;
  currency: string | null;
  usd_value: number | string | null;
  is_forward_looking: boolean;
  series_id: number | string;
  source_metric: string | null;
  metric_variant: string | null;
  period_type: string | null;
  source_period_raw: string | null;
  confidence: number | string | null;
  source_page: number | null;
  verification: string | null;
  acknowledged_by: string | null;
  ingested_at: string | null;
  series_point_count: number | string;
  source_point_count: number | string;
};

export type HistoricalTimeseriesResponse = {
  source: string;
  platform: "TECH_FUND";
  metric: string;
  scenario: string;
  basis: string | null;
  rows: HistoricalMetricPoint[];
  count: number;
  source_point_count: number;
  projection: "consistent_series" | "max_coverage";
};

/** Discover every promoted historical company metric available to the pivot UI. */
export function getHistoricalCoverage(
  scenario = "actual",
  includeFuture = false,
) {
  const qs = new URLSearchParams({ scenario });
  if (includeFuture) qs.set("include_future", "1");
  return request<HistoricalCoverageResponse>(
    `${reportingBase("gp")}/historical/coverage?${qs.toString()}`,
  );
}

/** Read one promoted historical metric across all companies and periods. */
export function getHistoricalMetricTimeseries(params: {
  metric: string;
  scenario?: string;
  basis?: string;
  from?: string;
  to?: string;
  includeFuture?: boolean;
  projection?: "consistent_series" | "max_coverage";
}) {
  const qs = new URLSearchParams({
    metric: params.metric,
    scenario: params.scenario ?? "actual",
  });
  if (params.basis) qs.set("basis", params.basis);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.includeFuture) qs.set("include_future", "1");
  if (params.projection) qs.set("projection", params.projection);
  return request<HistoricalTimeseriesResponse>(
    `${reportingBase("gp")}/historical/timeseries?${qs.toString()}`,
  );
}

export type HistoricalSnapshotLineage = {
  dataset_sha256: string;
  dataset_name: string;
  dataset_schema_version: string;
  dataset_status: string;
  query_key: string;
  query_version: string;
  query_definition_sha256: string;
  query_spec_sha256: string;
  result_sha256: string;
  output_contract_version: string;
  allowed_component_keys: string[];
  surface_key: string;
  is_surface_default: boolean;
  cohort_key: string | null;
  cohort_version: string | null;
  cohort_definition_sha256: string | null;
  row_count: number;
  as_of: string;
  generated_at: string;
  expires_at: string | null;
  coverage: Record<string, unknown>;
  caveats: unknown[];
};

export type ManagedHistoricalSnapshot = {
  result_ref: string;
  dataset: HistoricalDataset;
  analysis: HistoricalAnalysisSpec;
  lineage: HistoricalSnapshotLineage;
};

export type SavedHistoricalSnapshotUnion =
  | ManagedHistoricalSnapshot
  | HistoricalDashboardSnapshotV1;

export type SavedHistoricalAnalysis = {
  id: string;
  title: string | null;
  fund_id: string | null;
  created_at: string;
  updated_at: string;
  result_ref: string | null;
  analysis_id: string | null;
};

export type HistoricalWorkspaceResponse = {
  managed: boolean;
  fallback_reason: "no_published_snapshot" | null;
  snapshot: ManagedHistoricalSnapshot | null;
  saved_analyses: SavedHistoricalAnalysis[];
};

/**
 * Loads the latest published, server-owned Historical snapshot plus the current
 * user's saved analysis summaries. A 200 with managed=false means the reporting
 * contract is wired but no snapshot has been published yet.
 */
export function getHistoricalWorkspace() {
  return request<HistoricalWorkspaceResponse>(
    "/v1/gp/portfolio/techfund/historical/workspace",
  );
}

/** Persist only an approved spec + immutable result_ref; numeric values stay in reporting. */
export function saveHistoricalAnalysis(
  analysisSpec: HistoricalAnalysisSpec,
  resultRef: string,
  datasetVersion: string,
) {
  return request<{
    saved: true;
    conversation_id: string;
    result_ref: string;
    persisted_at: string;
  }>("/v1/gp/portfolio/techfund/historical/analyses", {
    method: "POST",
    body: JSON.stringify({
      analysis_spec: analysisSpec,
      result_ref: resultRef,
      dataset_version: datasetVersion,
    }),
  });
}

export function listSavedHistoricalAnalyses() {
  return request<{ saved_analyses: SavedHistoricalAnalysis[] }>(
    "/v1/gp/portfolio/techfund/historical/analyses",
  );
}

export function getSavedHistoricalAnalysis(conversationId: string) {
  return request<{
    conversation: Record<string, unknown>;
    messages: Array<Record<string, unknown>>;
    snapshot: SavedHistoricalSnapshotUnion | null;
    snapshots_by_result_ref: Record<string, SavedHistoricalSnapshotUnion>;
    active_revision_id?: string | null;
    active_revision?: HistoricalRevisionMetadata | null;
    revisions_by_id?: Record<string, HistoricalRevisionMetadata>;
  }>(`/v1/gp/portfolio/techfund/historical/analyses/${encodeURIComponent(conversationId)}`);
}

export type HistoricalRevisionRequest =
  | { schemaVersion: "historical-dashboard-action/v1"; requestId: string; conversationId: string; baseRevisionId: string; patch: HistoricalDashboardPatch }
  | { schemaVersion: "historical-dashboard-undo/v1"; requestId: string; conversationId: string; baseRevisionId: string }
  | { schemaVersion: "historical-dashboard-fork/v1"; requestId: string; sourceConversationId: string; sourceRevisionId: string };

/** A caller retains this exact body and request ID until its terminal outcome is known. */
export async function mutateHistoricalDashboard(body: HistoricalRevisionRequest): Promise<HistoricalRevisionResponse> {
  const action = body.schemaVersion === "historical-dashboard-action/v1" ? "action"
    : body.schemaVersion === "historical-dashboard-undo/v1" ? "undo" : "fork";
  const result = await request<unknown>(`/v1/gp/portfolio/techfund/analysis/${action}`, { method: "POST", body: JSON.stringify(body) });
  if (!isHistoricalRevisionResponse(result, body.requestId, "conversationId" in body ? body.conversationId : undefined)) {
    throw new Error("The revision outcome could not be verified. Reload the thread to check its saved state.");
  }
  if (action === "fork" && result.terminal.status === "persisted" && result.conversationId === (body as Extract<HistoricalRevisionRequest, { sourceConversationId: string }>).sourceConversationId) {
    throw new Error("The fork destination could not be verified.");
  }
  return result;
}

export type ReportingTimeseriesParams = {
  /** Required by the worker — a timeseries needs a metric to be meaningful. */
  metric: string;
  platform?: string;
  entity?: string;
  scenario?: string;
  basis?: string;
};

/**
 * GET /v1/gp/reporting/timeseries?metric=…&format=json. The worker reuses the
 * fact projection ordered by period_end and returns the SAME {rows,count}
 * envelope — there is no distinct {points:[…]} shape — so `points` is exposed
 * here as a convenience alias of `rows` for charting. `metric` is required.
 */
export function getReportingTimeseries(
  params: ReportingTimeseriesParams,
  scope: DocAdminScope = "gp",
) {
  const qs = new URLSearchParams();
  appendReportingParams(qs, params);
  qs.set("format", "json");
  return request<{ rows: ReportingFact[]; count: number; format?: string }>(
    `${reportingBase(scope)}/timeseries?${qs.toString()}`,
  ).then((res) => ({ ...res, points: res.rows }));
}

// ─── WS-W3: DDQ Workroom review API ────────────────────────────────
//
// GP reviewers step through every DDQ answer before attestation:
//   accept  — answer stands as-is
//   edit    — reviewer has edited the text; edited_text carries the new copy
//   flag    — needs more work; answer is blocked from attestation
//   pending — not yet reviewed (initial state)
//
// The attested-export-link endpoint mints a single-use signed URL only once
// all questions are accepted or edited (no pending / flagged rows). A 409 is
// returned when review is incomplete; we surface it as a typed error
// (DdqReviewIncompleteError) so the ReviewBar can render the escape-hatch
// prompt rather than a generic red toast.

export type DDQReviewState = "pending" | "accepted" | "edited" | "flagged";

export type DDQQuestionReview = {
  question_id: string;
  state: DDQReviewState;
  method?: "individual" | "bulk";
  edited_text: string | null;
  original_answer: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  attested_at: string | null;
};

export type DDQReviewCounts = {
  total: number;
  pending: number;
  accepted: number;
  edited: number;
  flagged: number;
  unanswered: number;
  stale: number;
  methods?: { individual: number; edited: number; bulk: number };
};

export type DDQAttestation = {
  attested_at: string;
  attested_by: string;
  with_exceptions: boolean;
};

/** Thrown by `createDdqAttestedExportLink` when the server returns HTTP 409
 *  with `{ review_complete: false, counts, stale_qids }`. The ReviewBar catches
 *  it to surface the escape-hatch prompt rather than a generic error toast. */
export class DdqReviewIncompleteError extends Error {
  readonly counts: DDQReviewCounts;
  readonly staleQids: string[];

  constructor(counts: DDQReviewCounts, staleQids: string[]) {
    super("DDQ review is not complete");
    this.name = "DdqReviewIncompleteError";
    this.counts = counts;
    this.staleQids = staleQids;
  }
}

/** GET /v1/gp/ddq/:id/reviews */
export function getDdqReviews(id: string): Promise<{
  reviews: DDQQuestionReview[];
  counts: DDQReviewCounts;
  review_complete: boolean;
  stale_qids: string[];
  reopened_after_attestation: boolean;
  last_attestation: DDQAttestation | null;
}> {
  return request(`/v1/gp/ddq/${encodeURIComponent(id)}/reviews`);
}

/** PUT /v1/gp/ddq/:id/reviews/:qid */
export function setDdqReview(
  id: string,
  questionId: string,
  body: { state: DDQReviewState; edited_text?: string },
): Promise<{ review: DDQQuestionReview; counts: DDQReviewCounts; review_complete: boolean }> {
  return request(
    `/v1/gp/ddq/${encodeURIComponent(id)}/reviews/${encodeURIComponent(questionId)}`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

/** POST /v1/gp/ddq/:id/reviews/bulk-accept */
export function bulkAcceptDdqReviews(
  id: string,
  body: { scope: "section" | "all_high_confidence"; section?: string },
): Promise<{ accepted_count: number; counts: DDQReviewCounts; review_complete: boolean }> {
  return request(
    `/v1/gp/ddq/${encodeURIComponent(id)}/reviews/bulk-accept`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** POST /v1/gp/ddq/:id/attested-export-link.
 *  Throws `DdqReviewIncompleteError` on HTTP 409 with `review_complete: false`.
 *  All other non-2xx statuses throw the generic request Error (status + payload). */
export async function createDdqAttestedExportLink(
  id: string,
  { format, allowExceptions }: { format?: string; allowExceptions?: boolean } = {},
): Promise<{
  url: string;
  format: string;
  template_fallback: boolean;
  expires_in: number;
  expires_at: string;
  exported_with_exceptions: boolean;
}> {
  try {
    return await request(
      `/v1/gp/ddq/${encodeURIComponent(id)}/attested-export-link`,
      {
        method: "POST",
        body: JSON.stringify({ format, allow_exceptions: allowExceptions }),
      },
    );
  } catch (err) {
    const typed = err as Error & { status?: number; payload?: unknown };
    if (
      typed.status === 409 &&
      typed.payload !== null &&
      typeof typed.payload === "object" &&
      (typed.payload as { review_complete?: boolean }).review_complete === false
    ) {
      const p = typed.payload as {
        counts?: DDQReviewCounts;
        stale_qids?: string[];
      };
      throw new DdqReviewIncompleteError(
        p.counts ?? { total: 0, pending: 0, accepted: 0, edited: 0, flagged: 0, unanswered: 0, stale: 0 },
        p.stale_qids ?? [],
      );
    }
    throw err;
  }
}
